import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { apiUrl } from "../utils/Configuration";
import axios from 'axios';
import { getNames, getCode } from 'country-list';
import { State, City } from 'country-state-city';
import * as PH from "ph-locations";
import { motion, AnimatePresence } from "framer-motion";

import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

//INIT
export const CHECK_PAYMENT_METHOD = 2;

//#region Others
export const TabButton = ({ tabKey, label, isActive, onClick }) => (
  <button
    key={tabKey}
    onClick={onClick}
    className={`
      relative whitespace-nowrap px-4 py-2 text-sm font-medium transition-all duration-200
      focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-color
      ${
        isActive
          ? "text-theme-color font-semibold"
          : "text-gray-500 hover:text-theme-color"
      }
    `}
  >
    {label}

    {/* Active indicator */}
    {isActive && (
      <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-theme-color rounded-full" />
    )}
  </button>
);
export const ImageCompressToBase64 = (file, quality = 0.7) => {
  return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0, img.width, img.height);

          // Compress to JPEG and remove prefix
          const base64String = canvas.toDataURL("image/jpeg", quality);
          resolve(base64String.split(",")[1]);
        };

        img.onerror = reject;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
};
export const FormatDateToWord = (value) => {
  if (!value) return "";

  const date = new Date(value);

  const formattedDate = date
    .toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })
    .replace(/(\w{3})/, "$1."); // Add period after month abbreviation

  return formattedDate; 
};
export const FormatDateToYMD = (value) => {
  if (!(value instanceof Date)) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
export const FormatDateToMDY = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (isNaN(date)) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${month}/${day}/${year}`;
};
export const Base64ToBlob = (base64, contentType = 'image/jpeg') => {
  if(!base64){
    return ""
  }

  const byteCharacters = atob(base64); // decode base64
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}
export const FormatValueToDecimal = (value) => {
  if(!value){
    return 0.00
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value));

  // const truncated = Math.trunc(Number(value) * 100) / 100;

  // return new Intl.NumberFormat("en-US", {
  //   minimumFractionDigits: 2,
  //   maximumFractionDigits: 2
  // }).format(truncated);
}
export const FormatValueToDecimalTrunc = (value) => {
  if(!value){
    return 0.00
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    roundingMode: 'trunc'
  }).format(Number(value));
}
export const FormatValueToWord = (value) => {
  if (!value || isNaN(value)) return "Zero";

  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

  const convertGroup = (n) => {
    let str = "";
    if (n >= 100) {
      str += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 10 && n <= 19) {
      str += teens[n - 10];
    } else {
      str += tens[Math.floor(n / 10)] + (n % 10 !== 0 && n > 10 ? "-" : "") + ones[n % 10];
    }
    return str.trim();
  };

  // Split into whole number and decimal
  const parts = value.toString().split(".");
  let wholeNumber = parseInt(parts[0]);
  let cents = parts[1] ? parts[1].padEnd(2, '0').substring(0, 2) : "00";

  if (wholeNumber === 0) return `Zero and ${cents} cents`;

  let result = "";

  // Handle Thousands
  if (wholeNumber >= 1000) {
    result += convertGroup(Math.floor(wholeNumber / 1000)) + " Thousand ";
    wholeNumber %= 1000;
  }

  // Handle Hundreds/Tens/Units
  result += convertGroup(wholeNumber);

  return `${result.trim()} and ${cents} cents`;
};
export const ToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
});
export const FormatTimeTo12Hour = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (isNaN(date)) return "";

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;

  return `${hours}:${minutes} ${ampm}`;
};
//#endregion












//#region File
export const DetectMimeTypeFromBase64 = (base64) => {
  if (!base64) return "application/octet-stream";

  const signatures = {
    "/9j/": "image/jpeg",
    "iVBORw0KGgo": "image/png",
    "R0lGOD": "image/gif",
    "JVBERi0": "application/pdf",
    "UEsDB": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
    "0M8R4KGx": "application/vnd.ms-excel", // xls
    "UEsDBBQABgA": "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  };

  for (const signature in signatures) {
    if (base64.startsWith(signature)) {
      return signatures[signature];
    }
  }

  return "application/octet-stream"; // true fallback
};

export const HandleFileUpload = (e) => {
  return new Promise((resolve, reject) => {
    const file = e.target.files?.[0];
    if (!file) {
      resolve(null);
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];

      resolve({
        base64: base64String,
        fileName: file.name,
        contentType: file.type,
      });
    };

    reader.onerror = reject;

    reader.readAsDataURL(file);
  });
};

export const DownloadFile = (value) => {
  const fileData = value;
  if (!fileData) return;

  const { base64, fileName, contentType } = fileData;

  const linkSource = `data:${contentType};base64,${base64}`;
  const downloadLink = document.createElement("a");

  downloadLink.href = linkSource;
  downloadLink.download = fileName; // 👈 keeps original filename & extension
  downloadLink.click();
};
//#endregion









//#region Search
export const Searchbar = () => {
  return (
    <div className="flex items-center bg-gray-50 rounded-md px-4 py-2 w-full max-w-md border border-theme-color">
      <SearchIcon className="text-theme-color mr-2" />
      <input
          type="text"
          placeholder="Search..."
          className="bg-transparent outline-none w-full text-gray-700"
      />
    </div>
);
};
//#endregion















//#region Table
const getNestedValue = (tableName, obj, path, actions) => {
  const value = path.split('.').reduce((acc, part) => {
    if (!acc) return undefined;

    // Handle array access like 'employeeinformations[0]'
    const match = part.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      const [, arrayKey, index] = match;
      return acc[arrayKey]?.[parseInt(index, 10)];
    }

    return acc[part];
  }, obj);

  const getCurrency = JSON.parse(localStorage.getItem("currency") || "{}");

  if(tableName === "Vendor" || tableName === "Customer")
  {
    if (path === 'email') {
      if(!obj?.email){
        return "-"
      }

      return (
      <>
        <div className='flex flex-col gap-2'>
          { obj?.email && <div className='flex flex-row gap-2 items-center'>
            <EmailIcon style={{fontSize:"14px"}}/>
            <span>{`${obj?.email ? obj?.email : ""}`}</span>
          </div>}
        </div>
      </>
    )}

    if (path === 'contacts') {
      if(!obj?.mobile && !obj?.telephone){
        return "-"
      }

      return (
      <>
        <div className='flex flex-col gap-2'>
          {obj?.mobile && <div className='flex flex-row gap-2 items-center'>
            <PhoneIphoneIcon style={{fontSize:"14px"}}/>
            <span>{`${obj?.mobile ? obj?.mobile : ""}`}</span>
          </div>}
          {obj?.telephone && <div className='flex flex-row gap-2 items-center'>
            <LocalPhoneIcon style={{fontSize:"14px"}}/>
            <span>{`${obj?.telephone ? obj?.telephone : ""}`}</span>
          </div>}
        </div>
      </>
    )}
  }

  if(tableName === "Expense"){
    if(path === "expenseAccounts"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Expense Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Expense Account").icon}
          </button>
        </div>
      </>
      )
    }
    if(path === "paidThrough"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${obj?.paidThroughAccountName}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.paidAmount)}`}</span>
          </div>
        </div>
      </>
      )
    }
    if(path === "expense"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`Reference #: ${obj?.referenceNumber}`}</span>
          <span className='text-xs'>{`${FormatDateToWord(obj?.expenseDate)}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalAmount)}`}</span>
          </div>
        </div>
      </>
      )
    }
    if(path === "adjustments"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`VAT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.vat ?? 0)}`}</span>
          <span className='text-xs'>{`Discounts: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.discounts ?? 0)}`}</span>
          <span className='text-xs'>{`EWT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.ewt ?? 0)}`}</span>
        </div>
      </>
      )
    }
  }

  if(tableName === "Expense Accounts"){
    if(path === "adjustments"){
      const vatAmount = obj?.vat;
      const discountAmount = obj?.discounts;
      const ewtAmount = obj?.ewt;
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`VAT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(vatAmount)}`}</span>
          <span className='text-xs'>{`Discounts: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(discountAmount)}`}</span>
          <span className='text-xs'>{`EWT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(ewtAmount)}`}</span>
        </div>
      </>
      )
    }
  }

  if(tableName === "GL Account"){
    if(path === "transactions"){
      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "Transactions").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"rransactions"}
          >
            {actions.find(i => i.name === "Transactions")?.icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "account"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${obj?.accountNumber}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.accountName}`}</span>
          </div>
        </div>
      </>
      )
    }

    if (path === "glAccountTransactionsDebitAmount") {
      if (!value) {
        return (
          <span className={`text-gray-600`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      return (
        <span className={`text-green-500`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>
      )
    }
    if (path === "glAccountTransactionsCreditAmount" ) {
      if (!value) {
        return (
          <span className={`text-gray-600`}>{`- ${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      return (
        <span className={`text-red-500`}>{`- ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>
      )
    }
    if (path === "glAccountTransactionsBalance" ) {
      if (!value) {
        return (
          <span className={`text-gray-600`}>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      if(value < 0) {
        return (
          <span className={`text-red-500`}>{`- ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(Math.abs(value))}`} </span>
        )
      }

      return (
        <span className={`text-green-500`}>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>
      )
    }
  }

  if(tableName === "GL Account Transaction"){
    if(path === "details"){
      if(!obj?.company){
        return "-"
      }

      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${obj?.company?.companyId}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.company?.companyName1 ? obj?.company?.companyName1 : ""}`}</span>
          </div>
          <span className='text-xs'>{`${FormatDateToWord(obj?.date)}`}</span>
        </div>
      </>
    )}

    if(path === "account"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`Acc #: ${obj?.glAccountNumber}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.glAccountName}`}</span>
          </div>
        </div>
      </>
      )
    }

    if (path === "debit") {
      const _value = obj?.debitCredit === "Debit" ? obj?.amount : 0;

      if (!_value) {
        return (
          <span className={`text-gray-600`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      return (
        <span className={`text-green-500`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(_value)}`} </span>
      )
    }
    if (path === "credit" ) {
      const _value = obj?.debitCredit === "Credit" ? obj?.amount : 0;

      if (!_value) {
        return (
          <span className={`text-gray-600`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      return (
        <span className={`text-red-500`}>{`- ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(_value)}`} </span>
      )
    }
  }

  if(tableName === "Dashboard Recent Transactions"){
    if(path === "details"){
      if(!obj?.company){
        return "-"
      }

      return (
      <>
        <div className='flex flex-col'>
          <span className='text-base font-bold'>{`${obj?.company?.companyName1 ? obj?.company?.companyName1 : ""}`}</span>
          <span className='text-xs'>{`${FormatDateToWord(obj?.date)}`}</span>
        </div>
      </>
    )}

    if(path === "reference"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`Acc #: ${obj?.glAccountNumber}`}</span>
          <span className='text-base font-bold'>{`${obj?.glAccountName}`}</span>
          
          <span className='text-xs'>{`${obj?.referenceTypeDescription ?? ""}: ${obj?.referenceNumber}`}</span>
        </div>
      </>
      )
    }

    if (path === "debit") {
      const _value = obj?.debitCredit === "Debit" ? obj?.amount : 0;

      if (!_value) {
        return (
          <span className={`text-gray-600`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      return (
        <div className="flex flex-col gap-1">
          <span>{`${obj.description}`} </span>
          <span className={`text-green-500`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(_value)}`} </span>
        </div>
      )
    }

    if (path === "credit" ) {
      const _value = obj?.debitCredit === "Credit" ? obj?.amount : 0;

      if (!_value) {
        return (
          <span className={`text-gray-600`}>{`+ ${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
        )
      }

      return (
        <div className="flex flex-col gap-1">
          <span>{`${obj.description}`} </span>
          <span className={`text-red-500`}>{`- ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(_value)}`} </span>
        </div>
      )
    }
  }

  if(tableName === "Bill"){
    // if(path === "billDetails"){
    //   if(value?.length <= 0){
    //     return "No accounts added"
    //   }

    //   return (
    //   <>
    //     <div className="flex space-x-2">
    //       <button
    //         type='button'
    //         onClick={() => actions.find(i => i.name === "View Invoice Account").function(obj)}
    //         className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
    //         title={"view accounts"}
    //       >
    //         {actions.find(i => i.name === "View Invoice Account").icon}
    //       </button>
    //     </div>
    //   </>
    //   )
    // }

    if (path === "billDetails") {
      const orders = obj.orders || [];

      if (orders.length === 0) {
        return (
          <span className="text-xs text-gray-400 italic">No orders linked</span>
        );
      }

      // Helper to format currency/decimals
      const formatAmount = (num) =>
        Number(num || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

      const visibleCount = 2; // Number of items visible directly in the cell
      const visibleOrders = orders.slice(0, visibleCount);

      return (
        <div className="flex flex-col gap-1 py-1 max-w-[240px]">
          {/* 1. Visible Orders */}
          {visibleOrders.map((order, index) => (
            <div
              key={order.id || index}
              className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 rounded-md border border-gray-100 hover:bg-gray-100/80 transition-colors"
            >
              <span className="font-mono text-xs font-semibold text-gray-700 truncate" title={order.grNumber}>
                {order.grNumber || "N/A"}
              </span>
              <span className="text-xs font-bold text-gray-900 shrink-0">
                {`${getCurrency?.symbol ? getCurrency?.symbol : ""}${formatAmount(order.amount)}`}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if(path === "billNumber"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.billNumber}`}</span>
          </div>
          <span className='text-xs'>{`Order #: ${obj?.accountName ? obj?.accountName : "N/A"}`}</span>
          <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalAmount)}`}</span>
        </div>
      </>
      )
    }

    if(path === "paidThroughAccounts"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Paid Through Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Paid Through Account").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "amountDue")
    {
      // const formattedDate = FormatDateToWord(obj?.dueDate);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(obj?.dueDate);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      // let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          // statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          // statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          // statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        // statusClass = "text-green-600";
      }

      return (
        <div>
          {/* <div className="flex flex-col">
            <span>{formattedDate}</span>
            <span className={`text-xs font-semibold ${statusClass}`}>
              {statusText}
            </span>
          </div> */}

          {statusText !== "Paid" && <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>}
        </div>
      )
    }

    if (path === "dueDate") {
      const formattedDate = FormatDateToWord(value);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(value);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        statusClass = "text-green-600";
      }

      return (
        <div className="flex flex-col">
          <span>{formattedDate}</span>
          <span className={`text-xs font-semibold ${statusClass}`}>
            {statusText}
          </span>
        </div>
      );
    }

    if(path === "adjustments"){
      const vatPercent = (obj?.vat ?? 0) / 100;
      const vatAmount = obj?.subTotal * vatPercent;
      const discountPercent = (obj?.discounts ?? 0) / 100;
      const discountAmount = obj?.subTotal * discountPercent;
      const zeroRatedPercent = (obj?.zeroRated ?? 0) / 100;
      const zeroRatedAmount = obj?.subTotal * zeroRatedPercent;
      const ewtPercent = (obj?.ewt ?? 0) / 100;
      const ewtAmount = obj?.subTotal * ewtPercent;
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`VAT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(vatAmount)}`}</span>
          <span className='text-xs'>{`Discounts: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(discountAmount)}`}</span>
          <span className='text-xs hidden'>{`Zero Rated: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(zeroRatedAmount)}`}</span>
          <span className='text-xs'>{`EWT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(ewtAmount)}`}</span>
        </div>
      </>
      )
    }

    if(path === "creditNotesAmount")
    {
      if(!value){
        return "-"
      }

      return (
        <div className="flex flex-col">
          {
            obj?.creditNoteNumbers?.map((i, index) => {
              return <span key={index}>{i.creditNoteNumber}</span>
            })
          }
          <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>
          <div className="flex flex-col">
            <span>{`Old Amount: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalAmount + value)}`}</span>
          </div>
        </div>
      )
    }

    if(path === "goodReceiptGRNumber"){
      return (
      <>
        <span className='text-base font-bold'>{`${obj?.goodReceiptManualGRNumber || obj?.goodReceiptGRNumber}`}</span>
      </>
      )
    }
  }

  if(tableName === "Sales Invoice"){
    // if(path === "salesInvoiceDetails"){
    //   if(value?.length <= 0){
    //     return "No accounts added"
    //   }

    //   return (
    //   <>
    //     <div className="flex space-x-2">
    //       <button
    //         type='button'
    //         onClick={() => actions.find(i => i.name === "View Sales Invoice Account").function(obj)}
    //         className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
    //         title={"view accounts"}
    //       >
    //         {actions.find(i => i.name === "View Sales Invoice Account").icon}
    //       </button>
    //     </div>
    //   </>
    //   )
    // }

    if (path === "salesInvoiceDetails") {
      const orders = obj.orders || [];

      if (orders.length === 0) {
        return (
          <span className="text-xs text-gray-400 italic">No orders linked</span>
        );
      }

      // Helper to format currency/decimals
      const formatAmount = (num) =>
        Number(num || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

      const visibleCount = 2; // Number of items visible directly in the cell
      const visibleOrders = orders.slice(0, visibleCount);

      return (
        <div className="flex flex-col gap-1 py-1 max-w-[240px]">
          {/* 1. Visible Orders */}
          {visibleOrders.map((order, index) => (
            <div
              key={order.id || index}
              className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 rounded-md border border-gray-100 hover:bg-gray-100/80 transition-colors"
            >
              <span className="font-mono text-xs font-semibold text-gray-700 truncate" title={order.doNumber}>
                {order.doNumber || "N/A"}
              </span>
              <span className="text-xs font-bold text-gray-900 shrink-0">
                {`${getCurrency?.symbol ? getCurrency?.symbol : ""}${formatAmount(order.amount)}`}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if(path === "paidThroughAccounts"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Paid Through Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Paid Through Account").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "salesInvoiceNumber"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.salesInvoiceNumber}`}</span>
          </div>
          <span className='text-xs'>{`${obj?.accountName ? obj?.accountName : "N/A"}`}</span>
          <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalAmount)}`}</span>
        </div>
      </>
      )
    }

    if(path === "adjustments"){
      const vatPercent = (obj?.vat ?? 0) / 100;
      const vatAmount = obj?.subTotal * vatPercent;
      const discountPercent = (obj?.discounts ?? 0) / 100;
      const discountAmount = obj?.subTotal * discountPercent;
      const zeroRatedPercent = (obj?.zeroRated ?? 0) / 100;
      const zeroRatedAmount = obj?.subTotal * zeroRatedPercent;
      const ewtPercent = (obj?.ewt ?? 0) / 100;
      const ewtAmount = obj?.subTotal * ewtPercent;
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`VAT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(vatAmount)}`}</span>
          <span className='text-xs'>{`Discounts: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(discountAmount)}`}</span>
          <span className='text-xs hidden'>{`Zero Rated: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(zeroRatedAmount)}`}</span>
          <span className='text-xs'>{`EWT: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(ewtAmount)}`}</span>
        </div>
      </>
      )
    }

    if(path === "amountDue")
    {
      // const formattedDate = FormatDateToWord(obj?.dueDate);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(obj?.dueDate);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      // let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          // statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          // statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          // statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        // statusClass = "text-green-600";
      }

      return (
        <div>
          {/* <div className="flex flex-col">
            <span>{formattedDate}</span>
            <span className={`text-xs font-semibold ${statusClass}`}>
              {statusText}
            </span>
          </div> */}

          {statusText !== "Paid" && <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>}
        </div>
      )
    }

    if(path === "creditNotesAmount")
    {
      if(!value){
        return "-"
      }
      
      return (
        <div className="flex flex-col">
          {
            obj?.creditNoteNumbers?.map((i, index) => {
              return <span key={index}>{i.creditNoteNumber}</span>
            })
          }
          <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>
          <div className="flex flex-col">
            <span>{`Old Amount: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalAmount + value)}`}</span>
          </div>
        </div>
      )
    }

    if (path === "dueDate") {
      const formattedDate = FormatDateToWord(value);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(value);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        statusClass = "text-green-600";
      }

      return (
        <div className="flex flex-col">
          <span>{formattedDate}</span>
          <span className={`text-xs font-semibold ${statusClass}`}>
            {statusText}
          </span>
        </div>
      );
    }
  }

  if(tableName === "Delivery Order Detail"){

    if(path === "item"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.itemDescription}`}</span>
          </div>
          <span className='text-xs text-gray-400'>{`${obj?.itemCode ? obj?.itemCode : ""}`}</span>
          <span className='text-xs text-gray-400'>{`${obj?.brandName ? obj?.brandName : ""}`}</span>
        </div>
      </>
      )
    }

    if(path === "orderedQty"){
      return (
        <>
          {`${value} ${obj?.uomName || ""}`}
        </>
      )
    }

    if(path === "receivedQty"){
      return (
        <>
          {`${value} ${obj?.uomName || ""}`}
        </>
      )
    }

    if(path === "remainingQty"){
      return (
        <>
          {`${value} ${obj?.uomName || ""}`}
        </>
      )
    }

    if(path === "drAccount"){
      if(obj?.accounts[0]?.drAccount){
        return (
        <>
          <div className='flex flex-col'>
            <span className='text-xs'>{`Code: ${obj?.accounts[0]?.drAccountNumber ?? ""}`}</span>
            <div className='flex flex-row items-center'>
              <span className='text-base font-bold'>{`${obj?.accounts[0]?.drAccountName ?? ""}`}</span>
            </div>
          </div>
        </>
        )
      }

      return <>-</>
    }

    if(path === "crAccount"){
      if(obj?.accounts[0]?.crAccount){
        return (
        <>
          <div className='flex flex-col'>
            <span className='text-xs'>{`Code: ${obj?.accounts[0]?.crAccountNumber ?? ""}`}</span>
            <div className='flex flex-row items-center'>
              <span className='text-base font-bold'>{`${obj?.accounts[0]?.crAccountName ?? ""}`}</span>
            </div>
          </div>
        </>
        )
      }

      return <>-</>
    }
  }

  if(tableName === "Item Return"){

    if(path === "item"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.itemDescription}`}</span>
          </div>
          <span className='text-xs text-gray-400'>{`${obj?.itemCode ? obj?.itemCode : ""}`}</span>
          <span className='text-xs text-gray-400'>{`${obj?.brandName ? obj?.brandName : ""}`}</span>
        </div>
      </>
      )
    }

    if(path === "quantity"){
      const accounts = [
        { label: "ORDERED", name: `${obj.orderedQty} ${obj.uomName ?? ""}`, color: "text-blue-600" },
        { label: "RECEIVED", name: `${obj.receivedQty} ${obj.uomName ?? ""}`, color: "text-blue-600" },
        { label: "REMAINING", name: `${obj.remainingQty} ${obj.uomName ?? ""}`, color: "text-blue-600" },
      ];

      return (
        <div className="flex flex-col gap-1 py-1">
          {accounts.map((acc, i) => (
            <div key={i} className="flex items-center gap-11">
              <span className="text-[10px] font-bold w-7 text-gray-800 uppercase">
                {acc.label}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {acc.name || "—"}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if(path === "totals"){
      const accounts = [
        { label: "SUB", name: `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj.subTotal)}`, color: "text-blue-600" },
        { label: "TAX", name: `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj.taxAmount)}`, color: "text-blue-600" },
        { label: "DISC", name: `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj.totalDiscount)}`, color: "text-blue-600" },
        { label: "TOTAL", name: `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj.totalAmount)}`, color: "text-blue-600" },
      ];

      return (
        <div className="flex flex-col gap-1 py-1">
          {accounts.map((acc, i) => (
            <div key={i} className="flex items-center gap-2 leading-tight">
              <span className="text-[10px] font-bold w-7 text-gray-800 uppercase tracking-tighter">
                {acc.label}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {acc.name || "—"}
              </span>
            </div>
          ))}
        </div>
      );
    }
  }

  if(tableName === "Production"){

    if(path === "item"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.itemDescription}`}</span>
          </div>
          <span className='text-xs text-gray-400'>{`${obj?.itemCode ? obj?.itemCode : ""}`}</span>
          <span className='text-xs text-gray-400'>{`${obj?.brandName ? obj?.brandName : ""}`}</span>
        </div>
      </>
      )
    }

    if(path === "totalVolume"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.totalVolume}`}</span>
          </div>
          <span className='text-xs text-gray-400'>{`${obj?.uomName ? obj?.uomName : ""}`}</span>
        </div>
      </>
      )
    }
  }

  if(tableName === "Goods Receipt Detail"){

    if(path === "item"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.itemDescription}`}</span>
          </div>
          <span className='text-xs text-gray-400'>{`${obj?.itemCode ? obj?.itemCode : ""}`}</span>
          <span className='text-xs text-gray-400'>{`${obj?.brandName ? obj?.brandName : ""}`}</span>
        </div>
      </>
      )
    }

    if(path === "orderedQty"){
      return (
        <>
          {`${value} ${obj?.uomName || ""}`}
        </>
      )
    }

    if(path === "receivedQty"){
      return (
        <>
          {`${value} ${obj?.uomName || ""}`}
        </>
      )
    }

    if(path === "remainingQty"){
      return (
        <>
          {`${value} ${obj?.uomName || ""}`}
        </>
      )
    }

    if(path === "drAccount"){
      if(obj?.accounts[0]?.drAccount){
        return (
        <>
          <div className='flex flex-col'>
            <span className='text-xs'>{`Code: ${obj?.accounts[0]?.drAccountNumber ?? ""}`}</span>
            <div className='flex flex-row items-center'>
              <span className='text-base font-bold'>{`${obj?.accounts[0]?.drAccountName ?? ""}`}</span>
            </div>
          </div>
        </>
        )
      }

      return <>-</>
    }

    if(path === "crAccount"){
      if(obj?.accounts[0]?.crAccount){
        return (
        <>
          <div className='flex flex-col'>
            <span className='text-xs'>{`Code: ${obj?.accounts[0]?.crAccountNumber ?? ""}`}</span>
            <div className='flex flex-row items-center'>
              <span className='text-base font-bold'>{`${obj?.accounts[0]?.crAccountName ?? ""}`}</span>
            </div>
          </div>
        </>
        )
      }

      return <>-</>
    }

    if(path === "purchaseOrderNumber"){
      return (
        <>
          <span className=''>{`${obj?.manualPONumber || obj?.purchaseOrderNumber}`}</span>
        </>
      )
    }
  }

  if(tableName === "Sales Order Detail"
    || tableName === "Purchase Order Detail"
  ){
    if(path === "item"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.itemDescription}`}</span>
          </div>
          <span className='text-xs text-gray-400'>{`${obj?.brandName ? obj?.brandName : ""}`}</span>
        </div>
      </>
      )
    }
  }

  if(tableName === "Journal Line"){
    if(path === "postingKey"){
      return (
      <>
        <span>{`${obj?.postingKeyCode} - ${obj?.postingKeyDescription}`}</span>
      </>
      )
    }

    if(path === "debit"){
      return (
      <>
        <span>{`${obj?.postingKeyDebitCredit === "Debit" ? `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.amount)}` : `-`}`}</span>
      </>
      )
    }
    if(path === "credit"){
      return (
      <>
        <span>{`${obj?.postingKeyDebitCredit === "Credit" ? `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.amount)}` : `-`}`}</span>
      </>
      )
    }
  }

  if(tableName === "Journals"){
    if(path === "company"){
      if(!value){
        return <span className="italic text-xs">No vendor / customer added</span>
      }

      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${value?.companyId}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${value?.companyName1 ? value?.companyName1 : ""}`}</span>
          </div>
        </div>
      </>
      )
    }

    if(path === "amount"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`Debit: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalDebitAmount)}`}</span>
          <span className='text-xs'>{`Credit: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalCreditAmount)}`}</span>
          <span className='text-base font-bold'>{`NET: ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.totalNetAmount)}`}</span>
        </div>
      </>
      )
    }

    if(path === "journalLines"){
      if(value?.length <= 0){
        return "No lines added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Lines").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view lines"}
          >
            {actions.find(i => i.name === "View Lines").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "documentNumber"){
      return (
      <>
        <div className='flex flex-col gap-2'>
          <span className='text-base font-bold'>{`${obj?.documentNumber}`}</span>
          <div>
            { obj?.documentTypeDescription === "Accounts Payable" &&
              <span className={`bg-orange-400 text-white px-2 py-1 rounded-md`}>
                {obj?.documentTypeDescription}
              </span>
            }
            { obj?.documentTypeDescription === "Accounts Receivable" &&
              <span className={`bg-blue-400 text-white px-2 py-1 rounded-md`}>
                {obj?.documentTypeDescription}
              </span>
            }
            { obj?.documentTypeDescription === "General Journal" &&
              <span className={`bg-gray-400 text-white px-2 py-1 rounded-md`}>
                {obj?.documentTypeDescription}
              </span>
            }
          </div>
        </div>
      </>
      )
    }
    
    if(path === "documentTypeDescription"){
      if(!value){
        return "-"
      }

      if(value === "Accounts Payable"){
        return (
          <span className={`bg-orange-400 text-white px-3 py-1 rounded-full`}>
            {value}
          </span>
        )
      }

      if(value === "Accounts Receivable"){
        return (
          <span className={`bg-blue-400 text-white px-3 py-1 rounded-full`}>
            {value}
          </span>
        )
      }

      if(value === "General Journal"){
        return (
          <span className={`bg-gray-400 text-white px-3 py-1 rounded-full`}>
            {value}
          </span>
        )
      }

      return (
      <>
        <span className={`bg-gray-400 text-white px-3 py-1 rounded-full`}>
          {value}
        </span>
      </>
      )
    }
    
    if(path === "referenceNumber"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.referenceNumber ? obj?.referenceNumber : "-"}`}</span>
          </div>
          <span className='text-xs'>{`${obj?.documentDate ? FormatDateToWord(obj?.documentDate) : ""}`}</span>
        </div>
      </>
      )
    }
  }

  if(tableName === "Bill Payment"){
    if(path === "paymentNumber"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-base'>{`${obj?.manualInvoiceNumber ?? ""}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.paymentNumber}`}</span>
          </div>
        </div>
      </>
      )
    }

    if(path === "billPaymentDetails"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Bill Payment Detail").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view detail"}
          >
            {actions.find(i => i.name === "View Bill Payment Detail").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "paidThroughAccounts"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Paid Through Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Paid Through Account").icon}
          </button>
        </div>
      </>
      )
    }

    if (path === "dueDate") {
      const formattedDate = FormatDateToWord(value);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(value);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        statusClass = "text-green-600";
      }

      return (
        <div className="flex flex-col">
          <span>{formattedDate}</span>
          <span className={`text-xs font-semibold ${statusClass}`}>
            {statusText}
          </span>
        </div>
      );
    }

    if(path === "paidThrough"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.amount)}`}</span>
          </div>
          <span className='text-xs'>{`Received Date: ${FormatDateToWord(obj?.paymentDate)}`}</span>
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-400 hover:bg-sky-500 mt-2`}
            title={"view detail"}
          >
            View
          </button>
        </div>
      </>
      )
    }
  }

  if(tableName === "Bill Payment Detail"){
    if (path === "references") {
      const orders = obj.reference?.orders || [];

      if (orders.length === 0) {
        return (
          <span className="text-xs text-gray-400 italic">No orders linked</span>
        );
      }

      // Helper to format currency/decimals
      const formatAmount = (num) =>
        Number(num || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

      const visibleCount = 2; // Number of items visible directly in the cell
      const visibleOrders = orders.slice(0, visibleCount);

      return (
        <div className="flex flex-col gap-1 py-1 max-w-[240px]">
          {/* 1. Visible Orders */}
          {visibleOrders.map((order, index) => (
            <div
              key={order.id || index}
              className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 rounded-md border border-gray-100 hover:bg-gray-100/80 transition-colors"
            >
              <span className="font-mono text-xs font-semibold text-gray-700 truncate" title={order.grNumber}>
                {order.grNumber || "N/A"}
              </span>
              <span className="text-xs font-bold text-gray-900 shrink-0">
                {`${getCurrency?.symbol ? getCurrency?.symbol : ""}${formatAmount(order.amount)}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
  }

  if(tableName === "Sales Invoice Payment Detail"){
    if (path === "references") {
      const orders = obj.reference?.orders || [];

      if (orders.length === 0) {
        return (
          <span className="text-xs text-gray-400 italic">No orders linked</span>
        );
      }

      // Helper to format currency/decimals
      const formatAmount = (num) =>
        Number(num || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

      const visibleCount = 2; // Number of items visible directly in the cell
      const visibleOrders = orders.slice(0, visibleCount);

      return (
        <div className="flex flex-col gap-1 py-1 max-w-[240px]">
          {/* 1. Visible Orders */}
          {visibleOrders.map((order, index) => (
            <div
              key={order.id || index}
              className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 rounded-md border border-gray-100 hover:bg-gray-100/80 transition-colors"
            >
              <span className="font-mono text-xs font-semibold text-gray-700 truncate" title={order.doNumber}>
                {order.doNumber || "N/A"}
              </span>
              <span className="text-xs font-bold text-gray-900 shrink-0">
                {`${getCurrency?.symbol ? getCurrency?.symbol : ""}${formatAmount(order.amount)}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
  }

  if(tableName === "Items"){
    if(path === "accounts"){
      const accounts = [
        { label: "SALES", name: obj.salesAccountName, color: "text-blue-600" },
        { label: "RET", name: obj.salesReturnAccountName, color: "text-blue-600" },
        { label: "DISC", name: obj.salesDiscountAccountName, color: "text-blue-600" },
        { label: "COG", name: obj.costOfGoodsAccountName, color: "text-blue-600" },
        { label: "INV", name: obj.inventoryAccountName, color: "text-emerald-600" },
        { label: "PUR", name: obj.purchaseAccountName, color: "text-amber-600" },
      ];

      return (
        <div className="flex flex-col gap-1 py-1">
          {accounts.map((acc, i) => (
            <div key={i} className="flex items-center gap-2 leading-tight">
              <span className="text-[10px] font-bold w-7 text-gray-400 uppercase tracking-tighter">
                {acc.label}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {acc.name || "—"}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if(path === "uomName"){
      const accounts = [
        { label: "BASE", name: obj.uomName, color: "text-blue-600" },
        { label: "PUR", name: obj.purchaseUomName, color: "text-blue-600" },
        { label: "SALES", name: obj.salesUomName, color: "text-blue-600" },
      ];

      return (
        <div className="flex flex-col gap-1 py-1">
          {accounts.map((acc, i) => (
            <div key={i} className="flex items-center gap-2 leading-tight">
              <span className="text-[10px] font-bold w-7 text-gray-400 uppercase tracking-tighter">
                {acc.label}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {acc.name || "—"}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if(path === "components"){
      if(!obj?.isBOM){
        return "-"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Components").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Components").icon}
          </button>
        </div>
      </>
      )
    }

    /* if (path === "price") {
      const symbol = getCurrency?.symbol || "";
      const renderPrice = (label, value, color) => (
        <div className="flex justify-between gap-3 text-xs border-b border-gray-50 last:border-0 pb-0.5">
          <span className="text-gray-500 font-normal">{label}</span>
          <span className={`font-bold ${color}`}>{symbol}{FormatValueToDecimal(value)}</span>
        </div>
      );

      return (
        <div className="flex flex-col min-w-[100px] py-1">
          {renderPrice("Selling", obj?.sellingPrice, "text-green-600")}
          {renderPrice("Purchase", obj?.purchasePrice, "text-slate-700")}
        </div>
      );
    } */
  }

  if(tableName === "Conversion"){
    if(path === "item"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.itemDescription}`}</span>
          </div>
          <span className='text-xs'>{`${obj?.itemBrandName ? obj?.itemBrandName : "N/A"}`}</span>
        </div>
      </>
      )
    }
  }

  if(tableName === "Sales Invoice Payment"){
    if(path === "paymentNumber"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-base'>{`${obj?.manualInvoiceNumber ?? ""}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.paymentNumber}`}</span>
          </div>
        </div>
      </>
      )
    }
    
    if(path === "salesInvoicePaymentDetails"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Reference").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view detail"}
          >
            {actions.find(i => i.name === "View Reference").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "paidThroughAccounts"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Paid Through Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Paid Through Account").icon}
          </button>
        </div>
      </>
      )
    }

    if (path === "dueDate") {
      const formattedDate = FormatDateToWord(value);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(value);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        statusClass = "text-green-600";
      }

      return (
        <div className="flex flex-col">
          <span>{formattedDate}</span>
          <span className={`text-xs font-semibold ${statusClass}`}>
            {statusText}
          </span>
        </div>
      );
    }

    if(path === "depositToAccount"){
      return (
      <>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.amount)}`}</span>
          </div>
          <span className='text-xs'>{`Received Date: ${FormatDateToWord(obj?.paymentDate)}`}</span>
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-400 hover:bg-sky-500 mt-2`}
            title={"view detail"}
          >
            View
          </button>
        </div>
      </>
      )
    }
  }

  if(tableName === "Credit Note"){
    if(path === "creditNoteDetails"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Credit Note Detail").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view detail"}
          >
            {actions.find(i => i.name === "View Credit Note Detail").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "paidThroughAccounts"){
      if(value?.length <= 0){
        return "No accounts added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Paid Through Account").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Paid Through Account").icon}
          </button>
        </div>
      </>
      )
    }

    if (path === "dueDate") {
      const formattedDate = FormatDateToWord(value);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDate = new Date(value);
      dueDate.setHours(0, 0, 0, 0);

      let statusText = "";
      let statusClass = "";

      if(obj.status !== "Paid"){
        if (dueDate.getTime() > today.getTime()) {
          statusText = "On Schedule";
          statusClass = "text-green-600";
        } else if (dueDate.getTime() === today.getTime()) {
          statusText = "Due Today";
          statusClass = "text-amber-500";
        } else {
          statusText = "Overdue";
          statusClass = "text-red-600";
        }
      } else {
        statusText = "Paid";
        statusClass = "text-green-600";
      }

      return (
        <div className="flex flex-col">
          <span>{formattedDate}</span>
          <span className={`text-xs font-semibold ${statusClass}`}>
            {statusText}
          </span>
        </div>
      );
    }

    if(path === "creditNoteDetailsAmount"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${FormatDateToWord(obj?.creditNoteDate)}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.creditNoteDetailsAmount)}`}</span>
          </div>
        </div>
      </>
      )
    }
  }

  if(tableName === "Purchase Orders"){
    if(path === "purchaseOrderDetails"){
      if(value?.length <= 0){
        return "No items added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Items").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Items").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "po"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`PO Date: ${FormatDateToWord(obj?.poDate)}`}</span>
          <span className='text-base font-bold'>{`${obj?.manualPONumber || obj?.poNumber}`}</span>
        </div>
      </>
      )
    }

    if (path === "subTotal") {
      const symbol = getCurrency?.symbol || "";
      let vatableAmount = obj?.subTotal

      if(obj?.taxAmount > 0){
        vatableAmount = vatableAmount - obj?.taxAmount
      }
      
      return (
        <div className="flex flex-col gap-1 py-1">

          {/* Tax line (Neutral/Plus) */}
          <div className="flex justify-between items-center text-xs border-gray-100 pb-1">
            <span className="text-gray-500">Vatable Amt:</span>
            <span className="font-medium text-gray-700">
              {symbol}{FormatValueToDecimal(vatableAmount)}
            </span>
          </div>

          {/* Tax line (Neutral/Plus) */}
          <div className="flex justify-between items-center text-xs border-b border-gray-100 pb-1">
            <span className="text-gray-500">Tax:</span>
            <span className="font-medium text-gray-700">
              + {symbol}{FormatValueToDecimal(obj?.taxAmount)}
            </span>
          </div>

          {/* Subtotal line */}
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Subtotal:</span>
            <span className="font-medium text-gray-700">
              {symbol}{FormatValueToDecimal(obj?.subTotal)}
            </span>
          </div>

          {/* Discount line (Red/Negative) */}
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Discount:</span>
            <span className="font-medium text-red-600">
              - {symbol}{FormatValueToDecimal(obj?.totalDiscount)}
            </span>
          </div>
          
        </div>
      );
    }
  }

  if(tableName === "Good Receipt"){
    if(path === "goodReceiptDetailLines"){
      if(value?.length <= 0){
        return "No items added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Items").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Items").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "gr"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`GR Date: ${FormatDateToWord(obj?.grDate)}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.manualGRNumber || obj?.grNumber}`}</span>
          </div>
        </div>
      </>
      )
    }

    if(path === "purchaseOrderNumber"){
      return (
      <>
        <div className='flex flex-col'>
          {obj?.goodReceiptDetails?.map((i, index) => {
            return (
              <span key={index} className=''>{`${i?.manualPONumber || i?.purchaseOrderNumber}`}</span>
            )
          })}
        </div>
      </>
      )
    }
  }

  if(tableName === "Delivery Order"){
    if(path === "deliveryOrderDetailLines"){
      if(value?.length <= 0){
        return "No items added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Items").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Items").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "do"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`DO Date: ${FormatDateToWord(obj?.doDate)}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.doNumber}`}</span>
          </div>
        </div>
      </>
      )
    }

    if (path === "salesOrderNumber") {
      return (
        <div className="flex flex-wrap gap-2">
          {obj?.deliveryOrderDetails?.map((i, index) => (
            <div 
              key={index} 
              className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm shadow-sm"
            >
              <span className="block font-mono font-bold text-blue-700">
                #{i.salesOrderNumber}
              </span>
              <span className="block text-[11px] text-slate-500 leading-none mt-1">
                {i.salesmanName ?? "N/A"}
              </span>
            </div>
          ))}
        </div>
      );
    }
  }

  if(tableName === "Sales Orders"){
    if(path === "salesOrderDetails"){
      if(value?.length <= 0){
        return "No items added"
      }

      return (
      <>
        <div className="flex space-x-2">
          <button
            type='button'
            onClick={() => actions.find(i => i.name === "View Items").function(obj)}
            className={`text-white p-[1px] rounded-md bg-sky-500 hover:bg-sky-600`}
            title={"view accounts"}
          >
            {actions.find(i => i.name === "View Items").icon}
          </button>
        </div>
      </>
      )
    }

    if(path === "so"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`SO Date: ${FormatDateToWord(obj?.soDate)}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.soNumber}`}</span>
          </div>
        </div>
      </>
      )
    }
    if (path === "subTotal") {
      const symbol = getCurrency?.symbol || "";
      let vatableAmount = obj?.subTotal

      if(obj?.taxAmount > 0){
        vatableAmount = vatableAmount - obj?.taxAmount
      }
      
      return (
        <div className="flex flex-col gap-1 py-1">

          {/* Tax line (Neutral/Plus) */}
          <div className="flex justify-between items-center text-xs border-gray-100 pb-1">
            <span className="text-gray-500">Vatable Amt:</span>
            <span className="font-medium text-gray-700">
              {symbol}{FormatValueToDecimal(vatableAmount)}
            </span>
          </div>

          {/* Tax line (Neutral/Plus) */}
          <div className="flex justify-between items-center text-xs border-b border-gray-100 pb-1">
            <span className="text-gray-500">Tax:</span>
            <span className="font-medium text-gray-700">
              + {symbol}{FormatValueToDecimal(obj?.taxAmount)}
            </span>
          </div>

          {/* Subtotal line */}
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Subtotal:</span>
            <span className="font-medium text-gray-700">
              {symbol}{FormatValueToDecimal(obj?.subTotal)}
            </span>
          </div>

          {/* Discount line (Red/Negative) */}
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Discount:</span>
            <span className="font-medium text-red-600">
              - {symbol}{FormatValueToDecimal(obj?.totalDiscount)}
            </span>
          </div>
          
        </div>
      );
    }
  }

  if(tableName === "Chart Of Accounts Transaction"){
    if(path === "debit"){
      return (
      <>
        <span>{`${obj?.debitCredit === "Debit" ? `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.amount)}` : `-`}`}</span>
      </>
      )
    }

    if(path === "credit"){
      return (
      <>
        <span>{`${obj?.debitCredit === "Credit" ? `${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(obj?.amount)}` : `-`}`}</span>
      </>
      )
    }
  }

  if(tableName === "Bank"){
    if(path === "account"){
      return (
      <>
        <span className='text-base font-bold'>{`${obj?.glAccountName}`}</span>
      </>
      )
    }

    if(path === "details"){
      return (
      <>
        <div className='flex flex-col'>
          <span>{`${obj?.swiftCode}`}</span>
          <span>{`${obj?.branch}`}</span>
          <span>{`${obj?.currency}`}</span>
        </div>
      </>
      )
    }

    if(path === "company"){
      return (
      <>
        <div className='flex flex-col'>
          <span>{`${obj?.companyName}`}</span>
        </div>
      </>
      )
    }

    if(path === "bank"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${obj?.accountNumber}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.bankName}`}</span>
          </div>
        </div>
      </>
      )
    }
  }

  if(tableName === "Bank Reconciliation"){
    if(path === "transaction"){
      return (
      <>
        <div className='flex flex-col'>
          <span className='text-xs'>{`${obj?.transactionCode}`}</span>
          <div className='flex flex-row items-center'>
            <span className='text-base font-bold'>{`${obj?.transactionDescription}`}</span>
          </div>
        </div>
      </>
      )
    }
  }

  if (path === 'isActive') {
    return (
      <span className={`${value ? "bg-green-500 text-white" : "bg-red-500 text-white"} px-2 rounded-full`}>
        {`${value ? "active" : "inactive"}`}
      </span>
    )
  }

  if(path === 'actions'){
    if(tableName === "Bill Payment" || tableName === "Expense"){
      if(obj.status === "Posted" || obj.status === "Disapproved"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
                return i.visible 
                  && (i.name === "View"
                    || (obj.status === "Posted" && i.name === "Void")
                  )
              }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      } else if(obj.status === "Void"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
                return i.visible 
                  && (i.name === "View")
              }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      } else {
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
              return i.visible
                && (i.name !== "Void")
            }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    }

    if(tableName === "Sales Invoice Payment"){
      if(obj.status === "Posted" || obj.status === "Disapproved"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
                return i.visible 
                  && (i.name === "View"
                    || (obj.status === "Posted" && i.name === "Void")
                  )
              }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      } else if(obj.status === "Void"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
                return i.visible 
                  && (i.name === "View")
              }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      } else {
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
              return i.visible
                && (i.name !== "Void")
            }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    }

    if(tableName === "Credit Note"){
      if(obj.status === "Closed"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => i.visible && (i.name === "View" || i.name === "Refund")).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }

      if(obj.status === "Draft"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => i.visible && (i.name === "View" || i.name === "Edit"  || i.name === "Post")).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }

      if(obj.status === "Refunded"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => i.visible && (i.name === "View")).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    }

    if(tableName === "Bill" || tableName === "Sales Invoice"){
      if(obj.status !== "Open"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
              return i.visible 
                && (i.name === "View"
                  || (i.name === "Void" && obj.status !== "Void" &&  obj.status !== "Cancelled" )
                )
            }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    }

    if(tableName === "Good Receipt" || tableName === "Delivery Order" 
      || tableName === "Delivery Return"
      || tableName === "Purchase Return"
    ){
      if(obj.status !== "Open"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
              return i.visible 
                && (i.name === "View"
                  || (i.name === "Void" && obj.status !== "Void" &&  obj.status !== "Cancelled" )
                )
            }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    }

    /* if(tableName === "Delivery Order"){
      if(obj.status !== "Open"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => i.visible && (i.name === "View")).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    } */

    if(tableName === "Purchase Orders" || tableName === "Sales Orders"){
      if(obj.status !== "Open"){
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
              return i.visible 
                && (i.name === "View"
                  || (i.name === "Void" && obj.status !== "Void" &&  obj.status !== "Cancelled" )
                )
            }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      } else {
        return (
          <div className="flex space-x-2">
            {actions.filter(i => {
              return i.visible
                && (i.name !== "Void")
            }).map((action, actionIndex) => (
              <button
                key={actionIndex}
                type='button'
                onClick={() => action.function(obj)}
                className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
                title={action.tooltip}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )
      }
    }

    return (
      <div className="flex space-x-2">
        {actions.filter(i => {
          return i.visible
        }).map((action, actionIndex) => (
          <button
            key={actionIndex}
            type='button'
            onClick={() => action.function(obj)}
            className={`text-white ${action.color} p-[1px] rounded-md ${action.className}`}
            title={action.tooltip}
          >
            {action.icon}
          </button>
        ))}
      </div>
    )
  }

  if (path === 'status') {
    if(value === "Pending" || value === "On Going" || value === "Open" || value === "Draft" || value === "Available"){
      return (
        <span className={`bg-gray-400 text-white px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }
    
    if(value === "Verified"){
      return (
        <span className={`bg-purple-200 text-purple-700 px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }

    if(value === "Credit Review" || value === "Partially Paid" || value === "Partial" || value === "Issued"){
      return (
        <span className={`bg-yellow-200 text-yellow-700 px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }

    if(value === "For Approval"){
      return (
        <span className={`bg-orange-200 text-orange-700 px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }

    if(value === "Approved" || value === "Paid" || value === "Posted" || value === "Dispatched" || value === "Cleared" || value === "Received"){
      return (
        <span className={`bg-green-500 text-white px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }

    if(value === "Disbursed"){
      return (
        <span className={`bg-blue-500 text-white px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }

    if(value === "Blacklisted" || value === "Closed"  || value === "Refunded" || value === "Stale"){
      return (
        <span className="bg-gray-700 text-white px-3 py-1 rounded-full line-through">
          {value}
        </span>
      )
    }

    if(value === "Disapproved" || value === "Bounced" || value === "Bounced 2x" || value === "Overpaid" || value === "Cancelled" || value === "Void"){
      return (
        <span className={`bg-red-500 text-white px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    }

    /* if(value === "For Approval"){
      return (
        <span className={`bg-yellow-500 text-white px-3 py-1 rounded-full`}>
          {value}
        </span>
      )
    } */

    return (
      <span className={`bg-gray-400 text-white px-3 py-1 rounded-full`}>
        {value}
      </span>
    )
  }

  if (path === 'days') {
    return `${value} days`;
  }

  if (path === 'interestRate' 
    || path === "penaltyRate" 
    || path === "ownedPercent" 
    || path === "rate" 
  ) {
    return `${value ? value : 0}%`;
  }

  if (path === 'createdDate'
    || path === "dateOpened"
    || path === "dueDate"
    || path === "date"
    || path === "receivedDate"
    || path === "repaymentScheduleDueDate"
    || path === "poDate"
    || path === "soDate"
    || path === "creditNoteDate"
    || path === "paymentDate"
    || path === "documentDate"
    || path === "postingDate"
    || path === "expenseDate"
    || path === "transactionDate"
    || path === "grDate"
    || path === "doDate"
    || path === "productionDate"
  ) {
    if(!value){
      return "-"
    }

    const formattedDate = FormatDateToWord(value);

    return <span>{formattedDate}</span>;
  }

  

  if (path === "principalAmount" 
    || path === "interestAmount" 
    || path === "totalAmount" 
    || path === "paidAmount" 
    || path === "initialDeposit" 
    || path === "balance" 
    || path === "amount" 
    || path === "balanceAfter" 
    || path === "amountOwned" 
    || path === "netProceeds" 
    || path === "totalPaid" 
    || path === "amountPaid" 
    || path === "otherFeesPaid" 
    || path === "penaltyPaid" 
    || path === "interestPaid" 
    || path === "principalPaid" 
    || path === "expenseAmount" 
    || path === "paidThroughAmount" 
    || path === "billAccountsAmount" 
    || path === "amountDue" 
    || path === "subTotal" 
    || path === "totalDiscount" 
    || path === "taxAmount" 
    || path === "amountDueBeforePayment" 
    || path === "billPaymentDetailsAmount" 
    || path === "billDetailsAmount" 
    || path === "salesInvoiceDetailsAmount" 
    || path === "salesInvoicePaymentDetailsAmount" 
    || path === "creditNoteDetailsAmount" 
    || path === "creditNoteDetailsAmountBeforeAdjust" 
    || path === "creditNotesAmount" 
    || path === "salesInvoice.amountPaid" 
    || path === "reference.paidAmount" 
    || path === "reference.amountPaid" 
    || path === "totalDebitAmount" 
    || path === "totalCreditAmount" 
    || path === "creditAmount" 
    || path === "unitPrice" 
    || path === "averageCost" 
  ) {
    if (!value) {
      return (
        <span>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}0.00`} </span>
      )
    }

    return (
      <span>{`${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(value)}`} </span>
    )
  }

  if (path === 'file') {
    if(!obj.fileName){
      return <span className='italic text-gray-400'>No file uploaded yet</span>
    }
    const foundAction = actions.find(i => i.name === 'File');
    return (
      <div className='flex flex-row items-center gap-2'>
        <button
          className="text-white p-[1px] rounded-md bg-sky-600 hover:bg-sky-700"
          title="Download File"
          onClick={() => foundAction.function(obj)}
        >
          {foundAction.icon}
        </button>
        <span>{obj.fileName}</span>
      </div>
    )
  }

  if (path === 'vendor') {
    if(!obj?.vendorId){
      return "-"
    }

    return (
    <>
      <div className='flex flex-col'>
        <span className='text-xs'>{`${obj?.vendorId}`}</span>
        <div className='flex flex-row items-center'>
          <span className='text-base font-bold'>{`${obj?.vendorName1 ? obj?.vendorName1 : "No name"}`}</span>
        </div>
      </div>
    </>
  )}

  if (path === 'customer') {
    if(!obj?.customerId){
      return "-"
    }

    return (
    <>
      <div className='flex flex-col'>
        <span className='text-xs'>{`${obj?.customerId}`}</span>
        <div className='flex flex-row items-center'>
          <span className='text-base font-bold'>{`${obj?.customerName1 ? obj?.customerName1 : "No name"}`}</span>
        </div>
      </div>
    </>
  )}

  if(!value) {
    return "-"
  }

  return value;
};

export const CreateTable = ({ 
  tableName, 
  headers, 
  accessors, 
  data, 
  emptyMessage = 'No data available', 
  actions, 
  currentPage, 
  setCurrentPage, 
  itemsPerPage, 
  totalItems 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const rowsPerPage = itemsPerPage;

  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const allowSort = false;
  const allowFirstAndLastPage = true;

  const handleSort = (accessor) => {
    if(allowSort){
      setSortConfig(prevConfig => {
        if (prevConfig.key === accessor) {
          // Cycle through: null -> ascending -> descending -> null ...
          if (prevConfig.direction === null) {
            return { key: accessor, direction: 'asc' };
          } else if (prevConfig.direction === 'asc') {
            return { key: accessor, direction: 'desc' };
          } else {
            return { key: null, direction: null };
          }
        } else {
          return { key: accessor, direction: 'asc' };
        }
      });
    }
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return '';
    if (sortConfig.direction === 'asc') return '▲';
    if (sortConfig.direction === 'desc') return '▼';
    return '';
  };

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return data?.slice(start, end) || [];
  }, [data, currentPage, rowsPerPage]);

  return (
    <>
      <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-auto">
        <table className="w-full text-left">
          <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
              {headers.map((header, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(accessors[index])}
                  className={`px-6 py-4 ${allowSort ? "cursor-pointer select-none" : ""}`}
                >
                  <div className="flex items-center gap-1 min-w-[100px]">
                    {header}
                    <span className="text-xs">{getSortSymbol(accessors[index])}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedData?.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-blue-50/30 transition cursor-pointer text-sm">
                  {accessors.map((accessor, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-6 py-4 max-w-[200px] overflow-hidden hover:overflow-auto whitespace-nowrap"
                    >
                      {(() => {
                        const rawValue = getNestedValue(tableName, row, accessor, actions);
                        const value = rawValue ?? "-";

                        const trueValues = ["True", "Yes", "Subscribed", "Active", "Approved"];
                        const falseValues = ["False", "No", "Unsubscribed", "Inactive", "Unapproved", "Rejected"];

                        const isTrue = trueValues.includes(value);
                        const isFalse = falseValues.includes(value);

                        const textClass =
                          accessor !== "description"
                            ? isTrue
                              ? "text-green-500 font-semibold"
                              : isFalse
                              ? "text-red-500 font-semibold"
                              : ""
                            : "";

                        const extraClass =
                          accessor === "activeUsers"
                            ? "text-green-500 font-semibold"
                            : accessor === "inActiveUsers"
                            ? "text-red-500 font-semibold"
                            : "";

                        return (
                          <span className={`${textClass} ${extraClass}`}>
                            {value}
                          </span>
                        );
                      })()}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} className="px-4 py-4 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-4 my-4">
            { allowFirstAndLastPage &&
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                <FirstPageIcon fontSize='small'/>
              </button>
            }

            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              <NavigateBeforeIcon fontSize='small'/>
            </button>

            <span className="text-gray-700">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              <NavigateNextIcon fontSize='small'/>
            </button>

            { allowFirstAndLastPage &&
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                <LastPageIcon fontSize='small'/>
              </button>
            }
          </div>
        )}
      </div>
    </>
  );
};

export const CreateTableNoPagination = ({
  tableName,
  headers,
  accessors,
  data,
  emptyMessage = "No data available",
  actions,
  maxHeight = "400px", // scroll height
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const allowSort = false;

  const handleSort = (accessor) => {
    if (!allowSort) return;

    setSortConfig((prev) => {
      if (prev.key === accessor) {
        if (prev.direction === "asc") return { key: accessor, direction: "desc" };
        if (prev.direction === "desc") return { key: null, direction: null };
        return { key: accessor, direction: "asc" };
      }
      return { key: accessor, direction: "asc" };
    });
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return "";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  return (
    <div
      className="w-full overflow-auto border border-gray-300 rounded-md"
      style={{ maxHeight }}
    >
      <table className="min-w-full table-auto text-left text-sm">
        {/* Sticky Header */}
        <thead className="bg-theme-color text-white font-semibold uppercase tracking-wider sticky top-0 z-10">
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                onClick={() => handleSort(accessors[index])}
                className={`px-4 py-3 ${
                  allowSort ? "cursor-pointer select-none" : ""
                }`}
              >
                <div className="flex items-center gap-1 min-w-[100px]">
                  {header}
                  <span className="text-xs">{getSortSymbol(accessors[index])}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data?.length > 0 ? (
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-t hover:bg-gray-100 odd:bg-white even:bg-gray-50"
              >
                {accessors.map((accessor, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-4 py-2 max-w-[180px] overflow-hidden hover:overflow-auto whitespace-nowrap"
                  >
                    {(
                      (() => {
                        const value =
                          getNestedValue(tableName, row, accessor, actions) ?? "-";

                        const isTrue =
                          ["True", "Yes", "Subscribed", "Active", "Approved"].includes(
                            value
                          );

                        const isFalse =
                          [
                            "False",
                            "No",
                            "Unsubscribed",
                            "Inactive",
                            "Unapproved",
                            "Rejected",
                          ].includes(value);

                        return (
                          <span
                            className={`
                              ${isTrue && accessor !== "description" ? "text-green-500 font-semibold" : ""}
                              ${isFalse && accessor !== "description" ? "text-red-500 font-semibold" : ""}
                              ${accessor === "activeUsers" ? "text-green-500 font-semibold" : ""}
                              ${accessor === "inActiveUsers" ? "text-red-500 font-semibold" : ""}
                            `}
                          >
                            {value}
                          </span>
                        );
                      })()
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headers.length}
                className="px-4 py-6 text-center text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const CreateTableSelectMode = ({
  tableName,
  headers,
  accessors,
  data,
  emptyMessage = 'No data available',
  actions,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalItems,
  selectedRow,
  setSelectedRow,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const rowsPerPage = itemsPerPage;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const allowSort = false;
  const allowFirstAndLastPage = true;

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleSort = (accessor) => {
    if (allowSort) {
      setSortConfig(prev => {
        if (prev.key === accessor) {
          if (prev.direction === null) return { key: accessor, direction: 'asc' };
          if (prev.direction === 'asc') return { key: accessor, direction: 'desc' };
          return { key: null, direction: null };
        } else {
          return { key: accessor, direction: 'asc' };
        }
      });
    }
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return data?.slice(start, end) || [];
  }, [data, currentPage, rowsPerPage]);

  return (
    <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-auto">
        <table className="w-full text-left">
          <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
            {/* NEW RADIO BUTTON COLUMN HEADER */}
            <th className="px-6 py-4 w-10">Select</th>

            {headers.map((header, index) => (
              <th
                key={index}
                onClick={() => handleSort(accessors[index])}
                className={`px-6 py-4 ${allowSort ? "cursor-pointer select-none" : ""}`}
              >
                <div className="flex items-center gap-1 min-w-[100px]">
                  {header}
                  <span className="text-xs">{getSortSymbol(accessors[index])}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
          {paginatedData?.length > 0 ? (
            paginatedData.map((row, rowIndex) => {
              const isSelected = selectedRow?.id === row.id;

              return (
                <tr
                  key={rowIndex}
                  onClick={() => setSelectedRow(row)}
                  className={`border-t cursor-pointer ${
                    isSelected
                      ? "bg-gray-300 text-gray-700 hover:bg-gray-400"
                      : rowIndex % 2 === 0
                        ? "bg-white hover:bg-gray-100"
                        : "bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  {/* NEW RADIO BUTTON CELL */}
                  <td className="px-6 py-4">
                    <input
                      type="radio"
                      name="table-select-radio"
                      checked={isSelected}
                      onChange={() => setSelectedRow(row)}
                      onClick={(e) => e.stopPropagation()} // Prevents double toggling
                      className="h-4 w-4 text-blue-600"
                    />
                  </td>

                  {/* OTHER COLUMNS */}
                  {accessors.map((accessor, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-4 py-2 max-w-[200px] overflow-hidden hover:overflow-auto whitespace-nowrap"
                    >
                      {(
                        (() => {
                          const value = getNestedValue(tableName, row, accessor, actions) ?? '-';
                          const isTrue = ['True', 'Yes', 'Subscribed', 'Active', 'Approved'].includes(value);
                          const isFalse = ['False', 'No', 'Unsubscribed', 'Inactive', 'Unapproved'].includes(value);

                          return (
                            <span
                              className={
                                `${isTrue ? "text-green-500 font-semibold" : ""} 
                                 ${isFalse ? "text-red-500 font-semibold" : ""}`
                              }
                            >
                              {value}
                            </span>
                          );
                        })()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={headers.length + 1} className="px-4 py-4 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4 my-4">
          {allowFirstAndLastPage && (
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              <FirstPageIcon fontSize="small" />
            </button>
          )}

          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            <NavigateBeforeIcon fontSize="small" />
          </button>

          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            <NavigateNextIcon fontSize="small" />
          </button>

          {allowFirstAndLastPage && (
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              <LastPageIcon fontSize="small" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const CreateTableMultipleSelectMode = ({
  tableName,
  headers,
  accessors,
  data,
  emptyMessage = 'No data available',
  actions,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalItems,
  // --- Changed for multiple selection ---
  selectedRows,      // Now an array of selected row objects
  setSelectedRows,   // Function to update the array
  // ------------------------------------
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const rowsPerPage = itemsPerPage;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const allowSort = false;
  const allowFirstAndLastPage = true;

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleSort = (accessor) => {
    if (allowSort) {
      setSortConfig(prev => {
        if (prev.key === accessor) {
          if (prev.direction === null) return { key: accessor, direction: 'asc' };
          if (prev.direction === 'asc') return { key: accessor, direction: 'desc' };
          return { key: null, direction: null };
        } else {
          return { key: accessor, direction: 'asc' };
        }
      });
    }
  };

  const getSortSymbol = (accessor) => {
    if (sortConfig.key !== accessor) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // --- New: Handle row selection ---
  const handleRowSelection = (row) => {
    // Check if the row is already selected
    const isSelected = selectedRows?.some(selected => selected.id === row.id);

    if (isSelected) {
      // If selected, remove it from the array
      setSelectedRows(prevSelected => prevSelected.filter(selected => selected.id !== row.id));
    } else {
      // If not selected, add it to the array
      setSelectedRows(prevSelected => [...prevSelected, row]);
    }
  };

  // --- New: Handle "Select All" ---
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // If checkbox is checked, add all current page data to selectedRows
      // Ensure no duplicates if some were already selected
      const newSelected = [...selectedRows];
      data.forEach(row => {
        if (!newSelected.some(selected => selected.id === row.id)) {
          newSelected.push(row);
        }
      });
      setSelectedRows(newSelected);
    } else {
      // If checkbox is unchecked, remove all current page data from selectedRows
      setSelectedRows(prevSelected => prevSelected.filter(
        selected => !data.some(row => row.id === selected.id)
      ));
    }
  };

  // --- Helper to check if all rows on current page are selected ---
  const isAllRowsSelected = data?.length > 0 && data.every(row =>
    selectedRows?.some(selected => selected.id === row.id)
  );

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return data?.slice(start, end) || [];
  }, [data, currentPage, rowsPerPage]);

  return (
    <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-auto">
        <table className="w-full text-left">
          <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
            {/* New: Checkbox for Select All */}
            <th className="px-6 py-4 w-10">
              <input
                type="checkbox"
                onChange={handleSelectAll}
                checked={isAllRowsSelected}
                className="form-checkbox h-4 w-4 text-blue-600 rounded"
              />
            </th>
            {headers.map((header, index) => (
              <th
                key={index}
                onClick={() => handleSort(accessors[index])}
                className={`px-6 py-4 ${allowSort ? "cursor-pointer select-none" : ""}`}
              >
                <div className="flex items-center gap-1 min-w-[100px]">
                  {header}
                  <span className="text-xs">{getSortSymbol(accessors[index])}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {paginatedData?.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={() => handleRowSelection(row)} // <-- make entire row clickable
              className={`border-t cursor-pointer ${
                selectedRows?.some(selected => selected.id === row.id)
                  ? "bg-gray-300 text-gray-700 hover:bg-gray-300"
                  : rowIndex % 2 === 0
                    ? "bg-white hover:bg-gray-100"
                    : "bg-gray-50 hover:bg-gray-100"
              }`}
            >
              {/* Checkbox */}
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedRows?.some(selected => selected.id === row.id)}
                  onChange={() => handleRowSelection(row)}
                  onClick={(e) => e.stopPropagation()} // prevent row click from toggling twice
                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
              </td>

              {/* Other columns */}
              {accessors.map((accessor, colIndex) => (
                <td key={colIndex} className="px-4 py-2 max-w-[200px] overflow-hidden hover:overflow-auto whitespace-nowrap">
                  {(
                    (() => {
                      const value = getNestedValue(tableName, row, accessor, actions) ?? '-';
                      const isTrue = ['True', 'Yes', 'Subscribed', 'Active', 'Approved'].includes(value);
                      const isFalse = ['False', 'No', 'Unsubscribed', 'Inactive', 'Unapproved'].includes(value);
                      return (
                        <span className={`${
                          isTrue && accessor !== "description" ? 'text-green-500 font-semibold' : ''
                        } ${
                          isFalse && accessor !== "description" ? 'text-red-500 font-semibold' : ''
                        }`}>
                          {value}
                        </span>
                      );
                    })()
                  )}
                </td>
              ))}
            </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length + 1} className="px-4 py-4 text-center text-gray-500"> {/* +1 for the new checkbox column */}
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4 my-4">
          {allowFirstAndLastPage && (
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              <FirstPageIcon fontSize="small" />
            </button>
          )}

          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            <NavigateBeforeIcon fontSize="small" />
          </button>

          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            <NavigateNextIcon fontSize="small" />
          </button>

          {allowFirstAndLastPage && (
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              <LastPageIcon fontSize="small" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
//#endregion












//#region Message Modal
export const SuccessMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative shadow-md flex items-start w-80" role="alert">
      <div className="flex-grow">
        <strong className="font-bold">Success! </strong>
        <span className="block sm:inline">{message}</span>
      </div>
      <button
        onClick={onClose}
        className="ml-4 text-green-700 hover:text-green-900 font-bold"
      >
        ×
      </button>
    </div>
  );
};

export const FailMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-md flex items-start w-80" role="alert">
      <div className="flex-grow">
        <strong className="font-bold">Error! </strong>
        <span className="block sm:inline">{message}</span>
      </div>
      <button
        onClick={onClose}
        className="ml-4 text-red-700 hover:text-red-900 font-bold"
      >
        ×
      </button>
    </div>
  );
};


export const ConfirmationModal = ({ visible, onClose, onSave, modalName, message }) => {
  if (!visible) return null;

  const handleSubmit = async () => {
    await onSave();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h2 className="text-2xl font-semibold mb-4">{modalName}</h2>

        <div className="space-y-5 mt-10 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px]">
          <p className="text-gray-700 text-base">{message}</p>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
            onClick={onClose}
          >
            No
          </button>
          <button
            className="px-4 py-2 bg-theme-color text-white rounded-md hover:bg-theme-color-h transition"
            onClick={handleSubmit}
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
};


export const ApproveConfirmationModal = ({
  visible,
  onClose,
  onSave,
  modalName,
  message,
}) => {
  const [progress, setProgress] = useState(100);
  const AUTO_CLOSE_MS = 5000;

  // Auto-close timer + progress bar
  useEffect(() => {
    if (!visible) return;

    setProgress(100);

    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const percent = Math.max(100 - (elapsed / AUTO_CLOSE_MS) * 100, 0);
      setProgress(percent);
    }, 30);

    const timeout = setTimeout(() => {
      onClose();
    }, AUTO_CLOSE_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  const handleSubmit = async () => {
    await onSave();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fadeIn overflow-hidden">

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <CheckCircleIcon className="text-green-500 text-6xl" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-green-600 mb-2">
          {modalName || "Approved Successfully"}
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center text-base mt-4">
          {message}
        </p>

        {/* Actions */}
        <div className="flex justify-center space-x-3 mt-8">
          <button
            className="px-5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
            onClick={onClose}
          >
            Close
          </button>

          <button
            className="px-6 py-2 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition"
            onClick={handleSubmit}
          >
            Continue
          </button>
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-200">
          <div
            className="h-full bg-green-500 transition-all duration-75"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

//#endregion













//#region Loading
export const Loading = ({ message = "Loading..." }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm">
      {/* Spinner */}
      <div className="w-12 h-12 border-4 border-theme-color border-t-transparent rounded-full animate-spin"></div>

      {/* Message */}
      <p className="mt-4 text-sm font-medium text-white">{message}</p>
    </div>
  );
};
//#endregion















//#region Selection
export const DateTimeSelect = ({ data, setData, isEditable }) => {
    const [value, setValue] = useState("");

    useEffect(() => {
        if (data) {
        // Ensure only the datetime part is used
        setValue(data.slice(0, 16)); // Keep "YYYY-MM-DDTHH:mm"
        } else {
        setValue("");
        }
    }, [data]);

    const handleChange = (e) => {
        const newValue = e.target.value; // Still in "YYYY-MM-DDTHH:mm"
        setValue(newValue);
        setData(newValue ? newValue + ":00" : ""); // Append seconds if needed
    };

    return (
        <input
        type="datetime-local"
        value={value}
        onChange={handleChange}
        disabled={!isEditable}
        className={`w-full p-2 border rounded text-sm 
            ${isEditable ? "bg-white" : "bg-gray-100 cursor-not-allowed"}
            ${value ? "text-[#686868]" : "text-gray-400"}
        `}
        />
    );
};
export const DateSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);

    useEffect(() => {
        setIsSelected(!!data && data !== 0);
    }, [data]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <input
                    type="date"
                    value={data ? data.split("T")[0] : ""}
                    onChange={(e) => setData(e.target.value)}
                    disabled={!isEditable}
                    className={`w-full p-2 border border-gray-300 rounded text-sm 
                        ${isEditable ? "bg-white" : "bg-gray-100 cursor-not-allowed"}
                        ${isSelected ? "text-[#686868]" : "text-gray-400"}
                    `}
                />
            </div>
        </div>
    );
};
export const DateSelectWithClear = ({ data, setData, isEditable }) => {
  const isSelected = !!data;

  const handleClear = () => {
    setData(null); // or "" depending on your backend
  };

  return (
    <div className="flex flex-col gap-1 relative">
      <input
        type="date"
        value={data ? data.split("T")[0] : ""}
        onChange={(e) => setData(e.target.value || null)}
        disabled={!isEditable}
        className={`w-full p-2 pr-8 border rounded text-sm 
          ${isEditable ? "bg-white" : "bg-gray-100 cursor-not-allowed"}
          ${isSelected ? "text-[#686868]" : "text-gray-400"}
        `}
      />

      {isEditable && isSelected && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-sm"
          title="Clear date"
        >
          ✕
        </button>
      )}
    </div>
  );
};
export const MonthSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
      <div>
        <select
          value={data || ""}
          onChange={handleSelectChange}
          disabled={!isEditable}
          className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
        >
          <option value="">Choose month</option>
          {months.map((month) => (
            <option key={month} value={month} className="text-gray-700">
              {month}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
export const YearSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  // Generate years: Current year down to 5 years ago
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
      <div>
        <select
          value={data || ""}
          onChange={handleSelectChange}
          disabled={!isEditable}
          className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
        >
          <option value="">Choose year</option>
          {years.map((year) => (
            <option key={year} value={year} className="text-gray-700">
              {year}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
export const GenderSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose gender
                </option>
                <option value="Male" className="text-gray-700">
                    Male
                </option>
                <option value="Female" className="text-gray-700">
                    Female
                </option>
            </select>
        </div>
    </div>
  );
};
export const ProductTypeSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    All
                </option>
                <option value="Single" className="text-gray-700">
                    Single Products
                </option>
                <option value="BOM" className="text-gray-700">
                    BOM Products
                </option>
            </select>
        </div>
    </div>
  );
};
export const PriceTypeSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose type
                </option>
                <option value="Exclusive" className="text-gray-700">
                    Exclusive
                </option>
                <option value="Inclusive" className="text-gray-700">
                    Inclusive
                </option>
                <option value="Reseller" className="text-gray-700">
                    Reseller
                </option>
                <option value="Zero Rated" className="text-gray-700">
                    Zero Rated
                </option>
            </select>
        </div>
    </div>
  );
};
export const DebitCreditSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose type
                </option>
                <option value="Debit" className="text-gray-700">
                    Debit
                </option>
                <option value="Credit" className="text-gray-700">
                    Credit
                </option>
            </select>
        </div>
    </div>
  );
};
export const DebitCreditSelectWithAll = ({ data, setData, isEditable }) => {
  const handleSelectChange = (event) => {
    setData(event.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                  text-gray-700
                `}
            >
                <option value="All">
                    All
                </option>
                <option value="Debit" className="text-gray-700">
                    Debit
                </option>
                <option value="Credit" className="text-gray-700">
                    Credit
                </option>
            </select>
        </div>
    </div>
  );
};
export const CivilStatusSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose status
                </option>
                <option value="Single" className="text-gray-700">
                    Single
                </option>
                <option value="Married" className="text-gray-700">
                    Married
                </option>
                <option value="Widowed" className="text-gray-700">
                    Widowed
                </option>
                <option value="Divorced" className="text-gray-700">
                    Divorced
                </option>
                <option value="Separated" className="text-gray-700">
                    Separated
                </option>
                <option value="Annulled" className="text-gray-700">
                    Annulled
                </option>
                <option value="Live-in" className="text-gray-700">
                    Live-in
                </option>
            </select>
        </div>
    </div>
  );
};
export const CycleTypeSelect = ({ data, setData, isEditable, setDays, setMonths, setYears, setDaysOfMonth, }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
    setDays(0);
    setMonths(0);
    setYears(0);
    setDaysOfMonth("");
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose type
                </option>
                <option value="Days" className="text-gray-700">
                    Days
                </option>
                <option value="Months" className="text-gray-700">
                    Months
                </option>
                <option value="Years" className="text-gray-700">
                    Years
                </option>
                <option value="DaysOfMonth" className="text-gray-700">
                    DaysOfMonth
                </option>
            </select>
        </div>
    </div>
  );
};
export const CompanyTypeSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose type
                </option>
                <option value="SoleProprietorship" className="text-gray-700">
                    Sole Proprietorship
                </option>
                <option value="Partnership" className="text-gray-700">
                    Partnership
                </option>
                <option value="Corporation" className="text-gray-700">
                    Corporation
                </option>
            </select>
        </div>
    </div>
  );
};
export const StatusSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="All" className="text-gray-700">
                    All
                </option>
                <option value="Pending" className="text-gray-700">
                    Pending
                </option>
                <option value="Approved" className="text-gray-700">
                    Approved
                </option>
                <option value="Disapproved" className="text-gray-700">
                    Disapproved
                </option>
            </select>
        </div>
    </div>
  );
};
export const ScheduleStatusSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="All" className="text-gray-700">
                    All
                </option>
                <option value="Pending" className="text-gray-700">
                    Pending
                </option>
                <option value="Due" className="text-gray-700">
                    Due
                </option>
                <option value="Overdue" className="text-gray-700">
                    Overdue
                </option>
                <option value="Paid" className="text-gray-700">
                    Paid
                </option>
                <option value="For Approval" className="text-gray-700">
                    For Approval
                </option>
            </select>
        </div>
    </div>
  );
};
export const CountrySelect = ({ data, setData, isEditable }) => {
  const countryNames = getNames().sort();
  
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    // This checks if data is present (not null, undefined, "", 0, or false)
    setIsSelected(!!data);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
      <div>
        <select
          value={data || ""} // Use the 'data' prop as the controlled value
          onChange={handleSelectChange}
          disabled={!isEditable}
          className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
            ${isSelected ? "text-gray-700" : "text-gray-400"}
            ${!isEditable ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
          `}
        >
          {/* Default placeholder option */}
          <option value="">
            Choose country
          </option>

          {/* Map over the sorted country names to create options */}
          {countryNames.map(name => (
            <option key={name} value={name} className="text-gray-700">
              {name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
export const StateSelect = ({ data, setData, isEditable, selectedCountryName }) => {
  const [isSelected, setIsSelected] = useState(false);

  // 1. Get the list of states based on the selected country
  const states = useMemo(() => {
    if (!selectedCountryName) {
      return []; 
    }
    const countryCode = getCode(selectedCountryName); 
    return State.getStatesOfCountry(countryCode); 
  }, [selectedCountryName]);

  // --- THE FIX START ---
  // Initialize ref with the CURRENT country to prevent reset on mount
  const prevCountryRef = useRef(selectedCountryName);

  // Reset state selection ONLY if country changes
  useEffect(() => {
    if (prevCountryRef.current !== selectedCountryName) {
      setData(""); 
      setIsSelected(false);
      
      // Update ref
      prevCountryRef.current = selectedCountryName;
    }
  }, [selectedCountryName, setData]);
  // --- THE FIX END ---

  // Track if a value is selected
  useEffect(() => {
    setIsSelected(!!data);
  }, [data]);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  const isDisabled = !isEditable || states.length === 0;

  const placeholder = selectedCountryName 
    ? (states.length > 0 ? "Choose state / province" : "No states / provinces")
    : "Choose a country first";

  return (
    <div className="flex flex-col gap-1">
      <div>
        <select
          value={data || ""}
          onChange={handleSelectChange}
          disabled={isDisabled}
          className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none
            focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
            ${isSelected ? "text-gray-700" : "text-gray-400"}
            ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
          `}
        >
          <option value="">{placeholder}</option>
          {states.map(state => (
            <option key={state.isoCode} value={state.name} className="text-gray-700">
              {state.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
export const CitySelect = ({ data, setData, isEditable, selectedCountryName, selectedStateName }) => {
  const [isSelected, setIsSelected] = useState(false);

  // Use useMemo for synchronous, universal data fetching
  const cities = useMemo(() => {
    if (!selectedCountryName || !selectedStateName) return [];
    
    const countryCode = getCode(selectedCountryName); 
    if (!countryCode) return [];

    const stateData = State.getStatesOfCountry(countryCode).find(
      (state) => state.name === selectedStateName
    );

    if (!stateData) return []; 

    return City.getCitiesOfState(countryCode, stateData.isoCode)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCountryName, selectedStateName]); 

  // --- THE FIX START ---
  // Initialize ref with the CURRENT state to prevent reset on mount
  const prevStateRef = useRef(selectedStateName);

  // Reset city selection ONLY if state changes
  useEffect(() => {
    if (prevStateRef.current !== selectedStateName) {
      setData(""); 
      setIsSelected(false);

      // Update ref
      prevStateRef.current = selectedStateName;
    }
  }, [selectedStateName, setData]);
  // --- THE FIX END ---

  // Track if a value is selected
  useEffect(() => {
    setIsSelected(!!data);
  }, [data]);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  const isDisabled = !isEditable || cities.length === 0;

  const placeholder = selectedStateName
    ? (cities.length > 0 ? "Choose city" : "No cities found")
    : "Choose a state first";

  return (
    <div className="flex flex-col gap-1">
      <div>
        <select
          value={data || ""}
          onChange={handleSelectChange}
          disabled={isDisabled}
          className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none 
            focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
            ${isSelected ? "text-gray-700" : "text-gray-400"}
            ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
          `}
        >
          <option value="">{placeholder}</option>
          {cities.map(city => (
            <option key={city.name} value={city.name} className="text-gray-700">
              {city.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
export const PHRegionSelect = ({ data, setData, isEditable }) => {
  // Memoize the region list to avoid recalculating on every render
  const regionList = useMemo(() => {
    return PH.regions.map(region => ({
      code: region.code,
      name: region.name,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    // Check if data is present (not null, undefined, "", 0, or false)
    setIsSelected(!!data);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
      <div>
        <select
          value={data || ""} // Controlled value
          onChange={handleSelectChange}
          disabled={!isEditable}
          className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
            ${isSelected ? "text-gray-700" : "text-gray-400"}
            ${!isEditable ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
          `}
        >
          {/* Default placeholder option */}
          <option value="">Choose region</option>

          {/* Map over the regions to create options */}
          {regionList.map(region => (
            <option key={region.code} value={region.name} className="text-gray-700">
              {region.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
export const PHProvinceSelect = ({ data, setData, isEditable, selectedRegion }) => {
  const [isSelected, setIsSelected] = useState(false);

  // 1. Find the region code
  const regionCode = useMemo(() => {
    if (!selectedRegion) return null;
    const regionObj = PH.regions.find(r => r.name === selectedRegion);
    return regionObj ? regionObj.code : null;
  }, [selectedRegion]);

  // 2. Get provinces
  const provinces = useMemo(() => {
    if (!regionCode) return [];
    return PH.provinces
      .filter(p => p.region === regionCode)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [regionCode]);

  // --- THE FIX START ---
  // We initialize the ref with the CURRENT regionCode to prevent reset on mount
  const prevRegionCodeRef = useRef(regionCode);

  useEffect(() => {
    // Only reset if the regionCode actually CHANGED compared to the last render
    if (prevRegionCodeRef.current !== regionCode) {
      setData("");
      setIsSelected(false);
      
      // Update the ref to the new code
      prevRegionCodeRef.current = regionCode;
    }
  }, [regionCode, setData]);
  // --- THE FIX END ---

  // Track if a province is selected
  useEffect(() => {
    setIsSelected(!!data);
  }, [data]);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value); 
  };

  const isDisabled = !isEditable || provinces.length === 0;

  let placeholder = "Choose region first";
  if (selectedRegion) {
    placeholder = provinces.length > 0 ? "Choose province" : "No provinces found";
  }

  return (
    <div className="flex flex-col gap-1">
      <select
        value={data || ""}
        onChange={handleSelectChange}
        disabled={isDisabled}
        className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none
          focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
          ${isSelected ? "text-gray-700" : "text-gray-400"}
          ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
        `}
      >
        <option value="">{placeholder}</option>
        {provinces.map(province => (
          // Note: Ensure value matches what you expect in 'data' (Name vs Code)
          <option key={province.code} value={province.name} className="text-gray-700">
            {province.name}
          </option>
        ))}
      </select>
    </div>
  );
};
export const PHCitySelect = ({ data, setData, isEditable, selectedProvince }) => {
  const [isSelected, setIsSelected] = useState(false);

  // Get province code from province name
  const provinceCode = useMemo(() => {
    if (!selectedProvince) return null;
    const province = PH.provinces.find(p => p.name === selectedProvince);
    return province ? province.code : null;
  }, [selectedProvince]);

  // Filter cities/municipalities by province code
  const cities = useMemo(() => {
    if (!provinceCode) return [];
    return PH.citiesMunicipalities
      .filter(city => city.province === provinceCode)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [provinceCode]);

  // --- THE FIX START ---
  // Initialize ref with the CURRENT provinceCode to prevent reset on mount
  const prevProvinceCodeRef = useRef(provinceCode);

  // Reset selection ONLY if province actually changes
  useEffect(() => {
    if (prevProvinceCodeRef.current !== provinceCode) {
      setData("");
      setIsSelected(false);
      
      // Update the ref to the new code so it doesn't trigger again until next change
      prevProvinceCodeRef.current = provinceCode;
    }
  }, [provinceCode, setData]);
  // --- THE FIX END ---

  // Track selection state
  useEffect(() => {
    setIsSelected(!!data);
  }, [data]);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value); // store city name
  };

  const isDisabled = !isEditable || cities.length === 0;

  let placeholder = "Choose province first";
  if (selectedProvince) {
    placeholder = cities.length > 0 ? "Choose city / municipality" : "No cities found";
  }

  return (
    <div className="flex flex-col gap-1">
      <select
        value={data || ""}
        onChange={handleSelectChange}
        disabled={isDisabled}
        className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none
          focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
          ${isSelected ? "text-gray-700" : "text-gray-400"}
          ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
        `}
      >
        <option value="">{placeholder}</option>
        {cities.map((city, index) => (
          <option key={index} value={city.name} className="text-gray-700">
            {city.name}
          </option>
        ))}
      </select>
    </div>
  );
};
export const DisbursementTypeSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/DisbursementType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose type </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const PaymentMethodSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/PaymentMethod/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose method </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const PaymentMethodSelect2 = ({data, setData, bank, setBank, referenceNumber, setReferenceNumber, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);
        setBank(null);
        setReferenceNumber("");

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/PaymentMethod/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
        _setData([])
        setBank(null)
        setReferenceNumber("")
      }
    }, [setBank, setReferenceNumber]);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose method </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const DocumentTypeSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/DocumentType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose type </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const DocumentTypeSelectWithAll = ({data, setData, isEditable}) => {
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/DocumentType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        text-gray-700
                    `}
                >
                    <option value=""> All </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const InvoiceStatusSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose status
                </option>
                <option value="Open" className="text-gray-700">
                    Open
                </option>
                <option value="Pending" className="text-gray-700">
                    Pending
                </option>
                <option value="Partially Paid" className="text-gray-700">
                    Partially Paid
                </option>
                <option value="Paid" className="text-gray-700">
                    Paid
                </option>
                <option value="Overpaid" className="text-gray-700">
                    Overpaid
                </option>
                <option value="Cancelled" className="text-gray-700">
                    Cancelled
                </option>
                <option value="Completed" className="text-gray-700">
                    Completed
                </option>
                <option value="Void" className="text-gray-700">
                    Void
                </option>
            </select>
        </div>
    </div>
  );
};
export const InvoiceStatusSelectWithAll = ({ data, setData, isEditable }) => {
  const handleSelectChange = (event) => {
    setData(event.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    text-gray-700
                `}
            >
                <option value="All">
                    All
                </option>
                <option value="Open" className="text-gray-700">
                    Open
                </option>
                <option value="Pending" className="text-gray-700">
                    Pending
                </option>
                <option value="Partially Paid" className="text-gray-700">
                    Partially Paid
                </option>
                <option value="Paid" className="text-gray-700">
                    Paid
                </option>
                <option value="Overpaid" className="text-gray-700">
                    Overpaid
                </option>
                <option value="Cancelled" className="text-gray-700">
                    Cancelled
                </option>
                <option value="Completed" className="text-gray-700">
                    Completed
                </option>
                <option value="Void" className="text-gray-700">
                    Void
                </option>
            </select>
        </div>
    </div>
  );
};
export const PaymentStatusSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose status
                </option>
                <option value="Open" className="text-gray-700">
                    Draft
                </option>
                <option value="Partially Paid" className="text-gray-700">
                    Void
                </option>
                <option value="Partially Paid" className="text-gray-700">
                    Void
                </option>
            </select>
        </div>
    </div>
  );
};
export const PaymentStatusSelectWithAll = ({ data, setData, isEditable }) => {
  const handleSelectChange = (event) => {
    setData(event.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    text-gray-700
                `}
            >
                <option value="All">
                    All
                </option>
                <option value="Draft" className="text-gray-700">
                    Draft
                </option>
                <option value="Pending" className="text-gray-700">
                    Pending
                </option>
                <option value="Posted" className="text-gray-700">
                    Posted
                </option>
                <option value="Disapproved" className="text-gray-700">
                    Disapproved
                </option>
                <option value="Void" className="text-gray-700">
                    Void
                </option>
            </select>
        </div>
    </div>
  );
};
export const AccountTypeCodeSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose code
                </option>
                <option value="BAL" className="text-gray-700">
                    BAL
                </option>
                <option value="(A/P) BAL" className="text-gray-700">
                    (A/P) BAL
                </option>
                <option value="(A/R) BAL" className="text-gray-700">
                    (A/R) BAL
                </option>
                <option value="P&L" className="text-gray-700">
                    P&L
                </option>
            </select>
        </div>
    </div>
  );
};



export const GLAccountSubTypeSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/GLAccountSubType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose type </option>
                    {_data.map(({ id, code, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${code} - ${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const GLAccountSubTypeSelectWithAll = ({data, setData, isEditable}) => {
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/GLAccountSubType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        text-gray-700}
                    `}
                >
                    <option value=""> All </option>
                    {_data.map(({ id, code, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${code} - ${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const GLAccountSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/GLAccount` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose account </option>
                    {_data.map(({ id, accountName }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${accountName}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const BankGLAccountSubTypeSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/GLAccountSubType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        const filteredBank = response.data?.filter(i => i.description === "Bank" || i.description === "Credit Card")
        _setData(filteredBank)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose type </option>
                    {_data.map(({ id, code, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const CompanySelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/Company/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose company </option>
                    {_data.map(({ id, name }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${name}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const BillingTypeSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/BillingType/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose type </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const CreditTermSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/CreditTerm/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose term </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const SalesOrganizationSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/SalesOrganization/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose organization </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const DistributionChannelSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/DistributionChannel/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose channel </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const CurrencySelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option className='text-gray-700' value="">Choose currency</option>
                <option className='text-gray-700' value="AED">AED – UAE Dirham</option>
                <option className='text-gray-700' value="ARS">ARS – Argentine Peso</option>
                <option className='text-gray-700' value="AUD">AUD – Australian Dollar</option>
                <option className='text-gray-700' value="BHD">BHD – Bahraini Dinar</option>
                <option className='text-gray-700' value="BRL">BRL – Brazilian Real</option>
                <option className='text-gray-700' value="CAD">CAD – Canadian Dollar</option>
                <option className='text-gray-700' value="CHF">CHF – Swiss Franc</option>
                <option className='text-gray-700' value="CNY">CNY – Chinese Yuan</option>
                <option className='text-gray-700' value="EGP">EGP – Egyptian Pound</option>
                <option className='text-gray-700' value="EUR">EUR – Euro</option>
                <option className='text-gray-700' value="GBP">GBP – British Pound</option>
                <option className='text-gray-700' value="HKD">HKD – Hong Kong Dollar</option>
                <option className='text-gray-700' value="IDR">IDR – Indonesian Rupiah</option>
                <option className='text-gray-700' value="INR">INR – Indian Rupee</option>
                <option className='text-gray-700' value="JPY">JPY – Japanese Yen</option>
                <option className='text-gray-700' value="KRW">KRW – South Korean Won</option>
                <option className='text-gray-700' value="KWD">KWD – Kuwaiti Dinar</option>
                <option className='text-gray-700' value="MXN">MXN – Mexican Peso</option>
                <option className='text-gray-700' value="MYR">MYR – Malaysian Ringgit</option>
                <option className='text-gray-700' value="NZD">NZD – New Zealand Dollar</option>
                <option className='text-gray-700' value="PHP">PHP – Philippine Peso</option>
                <option className='text-gray-700' value="QAR">QAR – Qatari Riyal</option>
                <option className='text-gray-700' value="RUB">RUB – Russian Ruble</option>
                <option className='text-gray-700' value="SAR">SAR – Saudi Riyal</option>
                <option className='text-gray-700' value="SGD">SGD – Singapore Dollar</option>
                <option className='text-gray-700' value="THB">THB – Thai Baht</option>
                <option className='text-gray-700' value="TRY">TRY – Turkish Lira</option>
                <option className='text-gray-700' value="USD">USD – US Dollar</option>
                <option className='text-gray-700' value="VND">VND – Vietnamese Dong</option>
                <option className='text-gray-700' value="ZAR">ZAR – South African Rand</option>

            </select>
        </div>
    </div>
  );
};
export const OrderStatusSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option className='text-gray-700' value="">Choose status</option>
                <option className='text-gray-700' value="Open">Open</option>
                {/* <option className='text-gray-700' value="Invoiced">Invoiced</option> */}
                <option className='text-gray-700' value="Closed">Closed</option>
                <option className='text-gray-700' value="Cancelled">Cancelled</option>
                <option className='text-gray-700' value="Void">Void</option>

            </select>
        </div>
    </div>
  );
};
export const OrderStatusSelectWithAll = ({ data, setData, isEditable }) => {
  const handleSelectChange = (event) => {
    setData(event.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                  text-gray-700}
                `}
            >
                <option className='text-gray-700' value="All">All</option>
                <option className='text-gray-700' value="Open">Open</option>
                <option className='text-gray-700' value="Partial">Partial</option>
                <option className='text-gray-700' value="Closed">Closed</option>
                <option className='text-gray-700' value="Cancelled">Cancelled</option>
                <option className='text-gray-700' value="Void">Void</option>

            </select>
        </div>
    </div>
  );
};
export const CreditNoteStatusSelectWithAll = ({ data, setData, isEditable }) => {
  const handleSelectChange = (event) => {
    setData(event.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                  text-gray-700}
                `}
            >
                <option className='text-gray-700' value="All">All</option>
                <option className='text-gray-700' value="Open">Open</option>
                <option className='text-gray-700' value="Draft">Draft</option>
                {/* <option className='text-gray-700' value="Invoiced">Invoiced</option> */}
                <option className='text-gray-700' value="Posted">Posted</option>
                <option className='text-gray-700' value="Refunded">Refunded</option>
                <option className='text-gray-700' value="Closed">Closed</option>
                <option className='text-gray-700' value="Void">Void</option>

            </select>
        </div>
    </div>
  );
};
export const CreditNoteTypeSelectWithAll = ({ data, setData, isEditable }) => {
  const handleSelectChange = (event) => {
    setData(event.target.value);
  };

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                  text-gray-700}
                `}
            >
                <option className='text-gray-700' value="All">All</option>
                <option className='text-gray-700' value="Bills">Bills</option>
                {/* <option className='text-gray-700' value="Invoiced">Invoiced</option> */}
                <option className='text-gray-700' value="Expenses">Expenses</option>
                <option className='text-gray-700' value="Void">Void</option>

            </select>
        </div>
    </div>
  );
};
export const TaxTypeSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option className='text-gray-700' value="">Choose type</option>
                <option className='text-gray-700' value="Exclusive">Tax Exclusive</option>
                <option className='text-gray-700' value="Inclusive">Tax Inclusive</option>

            </select>
        </div>
    </div>
  );
};
export const TaxationCodeSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/TaxationCode/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose tax </option>
                    {_data.map((item) => (
                    <option key={item.id} value={item.id} className='text-gray-700'>
                        {`${item.description} - Rate (${item.rate ?? 0} %)`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const TermsSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.description?.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/FromPOS/terms` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.description || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose terms </option>
                    {_data.map((item, index) => (
                    <option key={index} value={item.description} className='text-gray-700'>
                        {item.description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const DriversSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp?.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/FromPOS/drivers` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose driver </option>
                    {_data.map((item, index) => (
                    <option key={index} value={item} className='text-gray-700'>
                        {item}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const PlateNoSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp?.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/FromPOS/plate-no` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose plate no. </option>
                    {_data.map((item, index) => (
                    <option key={index} value={item} className='text-gray-700'>
                        {item}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const BusinessAreaSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/BusinessArea/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose area </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const SalesInvoiceOptionalCustomerSelect = ({data, setData, isEditable, customerId}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/SalesInvoice/customer-optional/${customerId}` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, [customerId]);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose document no </option>
                    {_data.map((item) => (
                    <option key={item.id} value={item.id} className='text-gray-700'>
                        {`${item.salesInvoiceNumber}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const PurchaseInvoiceOptionalVendorSelect = ({data, setData, isEditable, vendorId}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/Bill/vendor-optional/${vendorId}` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, [vendorId]);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose document no </option>
                    {_data.map((item) => (
                    <option key={item.id} value={item.id} className='text-gray-700'>
                        {`${item.billNumber}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const CategorySelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp?.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/FromPOS/categories` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose category </option>
                    {_data.map((item, index) => (
                    <option key={index} value={item} className='text-gray-700'>
                        {item}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const ChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SubAccountsSelect = ({
  visible, onClose, modalName,
  data, setData,
  parentId,
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/sub-account-selection/${parentId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [parentId]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRows)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRows([])
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableMultipleSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const RevenueChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/revenue` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const ExpenseChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/expense` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const AccountsPayablesChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/accounts-payable` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const AccountsReceivablesChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/accounts-receivable` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const DrAccountsReceivablesChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/dr-accounts-receivable` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CrAccountsReceivablesChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/cr-accounts-receivable` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const ContraRevenueChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/contra-revenue` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const ContraExpenseChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/contra-expense` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const PostingKeySelect = ({
  visible, onClose, modalName,
  data, setData,
  type
}) => {
  const headers = [
    'Code', 
    'Description', 
    'Debit / Credit', 
    'Account Type', 
    'Document Type', 
  ]
  const accessors = [
    'postingKeyCode', 
    'description', 
    'debitCredit', 
    'glAccountSubTypeDescription', 
    'documentTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.postingKeyCode.toLowerCase().includes(lowerValue) ||
        item.description.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      let url = `${apiUrl}/api/PostingKey`

      if(type === "AP"){
        url = `${apiUrl}/api/PostingKey/accounts-payable`
      } else if(type === "AR"){
        url = `${apiUrl}/api/PostingKey/accounts-receivable`
      } else if(type === "GJ"){
        url = `${apiUrl}/api/PostingKey/general-journal`
      } 

      const response = await axios.get(url, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Posting Key"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const BillPerVendorSelect = ({
  visible, onClose, modalName,
  data, setData,
  vendor
}) => {
  const headers = [
    'INVOICE NUMBER', 
    'ORDER NUMBER', 
    // 'DUE DATE', 
    'TOTAL AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT PAID', 
  ]
  const accessors = [
    'billNumber', 
    'billDetails', 
    // 'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.billNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Bill/vendor/${vendor?.vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Bill"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const BillPerVendorTamperedSelect = ({
  visible, onClose, modalName,
  data, setData,
  billPaymentHeader,
  vendor
}) => {
  const headers = [
    'INVOICE NUMBER', 
    'ORDER NUMBER', 
    // 'DUE DATE', 
    'TOTAL AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT PAID', 
  ]
  const accessors = [
    'billNumber', 
    'billDetails', 
    // 'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.billNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Bill/vendor/${vendor?.vendorId}/billPaymentHeader/${billPaymentHeader?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor, billPaymentHeader]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Bill"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesPerCustomerSelect = ({
  visible, onClose, modalName,
  data, setData,
  customer
}) => {
  const headers = [
    'SI NUMBER', 
    'ORDER/S', 
    // 'DUE DATE', 
    'SALES AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT RECEIVED', 
  ]
  const accessors = [
    'salesInvoiceNumber', 
    'salesInvoiceDetails', 
    // 'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesInvoice/customer/${customer?.customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Sales Invoice"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesPerCustomerTamperedSelect = ({
  visible, onClose, modalName,
  data, setData,
  salesInvoicePaymentHeader,
  customer
}) => {
  const headers = [
    'SI NUMBER', 
    'ORDER/S', 
    // 'DUE DATE', 
    'SALES AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT RECEIVED', 
  ]
  const accessors = [
    'salesInvoiceNumber', 
    'salesInvoiceDetails', 
    // 'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesInvoice/customer/${customer?.customerId}/salesInvoicePaymentHeader/${salesInvoicePaymentHeader?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer, salesInvoicePaymentHeader]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Sales Invoice"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CreditNoteExpensePerVendorSelect = ({
  visible, onClose, modalName,
  data, setData,
  vendor
}) => {
  const headers = [
    'EXPENSE NUMBER', 
    'REF NUMBER', 
    'TOTAL AMOUNT', 
    'TOTAL PAID', 
  ]
  const accessors = [
    'expenseNumber', 
    'referenceNumber', 
    'totalAmount', 
    'paidAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Expense/credit-note/vendor/${vendor?.vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CreditNoteExpensePerVendorTamperedSelect = ({
  visible, onClose, modalName,
  data, setData,
  salesInvoicePaymentHeader,
  vendor
}) => {
  const headers = [
    'EXPENSE NUMBER', 
    'REF NUMBER', 
    'TOTAL AMOUNT', 
    'TOTAL PAID', 
  ]
  const accessors = [
    'expenseNumber', 
    'referenceNumber', 
    'totalAmount', 
    'paidAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Expense/credit-note/vendor/${vendor?.vendorId}/salesInvoicePaymentHeader/${salesInvoicePaymentHeader?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor, salesInvoicePaymentHeader]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CreditNoteBillPerVendorSelect = ({
  visible, onClose, modalName,
  data, setData,
  vendor
}) => {
  const headers = [
    'INVOICE NUMBER', 
    'ORDER NUMBER', 
    'DUE DATE', 
    'TOTAL AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT PAID', 
  ]
  const accessors = [
    'billNumber', 
    'orderNumber', 
    'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Bill/credit-note/vendor/${vendor?.vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CreditNoteBillPerVendorTamperedSelect = ({
  visible, onClose, modalName,
  data, setData,
  salesInvoicePaymentHeader,
  vendor
}) => {
  const headers = [
    'INVOICE NUMBER', 
    'ORDER NUMBER', 
    'DUE DATE', 
    'TOTAL AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT PAID', 
  ]
  const accessors = [
    'billNumber', 
    'orderNumber', 
    'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Bill/credit-note/vendor/${vendor?.vendorId}/salesInvoicePaymentHeader/${salesInvoicePaymentHeader?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor, salesInvoicePaymentHeader]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CreditNoteSalesPerCustomerSelect = ({
  visible, onClose, modalName,
  data, setData,
  customer
}) => {
  const headers = [
    'SI NUMBER', 
    'ORDER NUMBER', 
    'DUE DATE', 
    'SALES AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT RECEIVED', 
  ]
  const accessors = [
    'salesInvoiceNumber', 
    'orderNumber', 
    'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesInvoice/credit-note/customer/${customer?.customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CreditNoteSalesPerCustomerTamperedSelect = ({
  visible, onClose, modalName,
  data, setData,
  salesInvoicePaymentHeader,
  customer
}) => {
  const headers = [
    'SI NUMBER', 
    'ORDER NUMBER', 
    'DUE DATE', 
    'SALES AMOUNT', 
    'AMOUNT DUE', 
    'AMOUNT RECEIVED', 
  ]
  const accessors = [
    'salesInvoiceNumber', 
    'orderNumber', 
    'dueDate', 
    'totalAmount', 
    'amountDue', 
    'amountPaid', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.salesInvoiceNumber.toLowerCase().includes(lowerValue) ||
        item.orderNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesInvoice/credit-note/customer/${customer?.customerId}/salesInvoicePaymentHeader/${salesInvoicePaymentHeader?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer, salesInvoicePaymentHeader]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by po number or order number..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesOrderSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'SO NUMBER', 
    'SO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'soNumber', 
    'soDate', 
    'totalAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.soNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesOrder` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Sales Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesOrderOpenSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'SO NUMBER', 
    'SO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'soNumber', 
    'soDate', 
    'totalAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.soNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesOrder/open` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Sales Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesOrderOpenMultipleSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'SO NUMBER', 
    'SO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'soNumber', 
    'soDate', 
    'totalAmount', 
  ]

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.soNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesOrder/open` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRows)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRows([])
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableMultipleSelectMode
                  tableName={"Sales Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesOrderOpenMultipleSelectByCustomer = ({
  visible, onClose, modalName,
  data, setData,
  customer
}) => {
  const headers = [
    'SO NUMBER', 
    'SO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'soNumber', 
    'soDate', 
    'totalAmount', 
  ]

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.soNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesOrder/open/customer/${customer?.customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })

      const returnedData = response.data;
      const finalData = returnedData.filter(
        so => !data?.some(selected => selected.id === so.id)
      );
        
      setList(finalData)
      setFilteredData(finalData)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer, data]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRows)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRows([])
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableMultipleSelectMode
                  tableName={"Sales Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={list?.length <= 0 ? true : false}
                  className={`px-5 py-2 rounded-lg text-white font-medium hover:opacity-90 transition ${list?.length <= 0 ? "bg-gray-300" : "bg-theme-color"}`}
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const PurchaseOrderSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'PO NUMBER', 
    'PO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'poNumber', 
    'poDate', 
    'totalAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.poNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/PurchaseOrder` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Purchase Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const PurchaseOrderOpenSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'PO NUMBER', 
    'PO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'poNumber', 
    'poDate', 
    'totalAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.poNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/PurchaseOrder/open` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Purchase Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const PurchaseOrderVendorSelect = ({
  visible, onClose, modalName,
  data, setData,
  vendor, isAll,
}) => {
  const headers = [
    'PO NUMBER', 
    'PO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'poNumber', 
    'poDate', 
    'totalAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.poNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/PurchaseOrder/open/vendor/${isAll ? "All" : vendor?.vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor, isAll]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Purchase Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const PurchaseOrderVendorMultipleSelectByVendor = ({
  visible, onClose, modalName,
  data, setData,
  vendor,
}) => {
  const headers = [
    'PO NUMBER', 
    'PO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'poNumber', 
    'poDate', 
    'totalAmount', 
  ]

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.poNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/PurchaseOrder/open/vendor/${vendor ? vendor?.vendorId : "All"}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      const returnedData = response.data;
      const finalData = returnedData.filter(
        so => !data?.some(selected => selected.id === so.id)
      );
        
      setList(finalData)
      setFilteredData(finalData)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor, data]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRows)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRows([])
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableMultipleSelectMode
                  tableName={"Purchase Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={list?.length <= 0 ? true : false}
                  className={`px-5 py-2 rounded-lg text-white font-medium hover:opacity-90 transition ${list?.length <= 0 ? "bg-gray-300" : "bg-theme-color"}`}
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const GoodReceiptSelect = ({
  visible, onClose, modalName,
  data, setData,
}) => {
  const headers = [
    'GR NUMBER', 
    'VENDOR', 
    'GR DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'grNumber', 
    'vendor', 
    'grDate', 
    'amount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.grNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GoodReceipt/status/All` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Good Receipt"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const DeliveryOrderSelect = ({
  visible, onClose, modalName,
  data, setData,
}) => {
  const headers = [
    'DO NUMBER', 
    'CUSTOMER', 
    'DO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'doNumber', 
    'customer', 
    'doDate', 
    'amount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.doNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/DeliveryOrder/status/All` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Delivery Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className={`px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition`}
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const GoodReceiptVendorSelect = ({
  visible, onClose, modalName,
  data, setData,
  vendor
}) => {
  const headers = [
    'GR NUMBER', 
    'GR DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'grNumber', 
    'grDate', 
    'amount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.grNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GoodReceipt/open/vendor/${vendor?.vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Good Receipt"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const DeliveryOrderCustomerSelect = ({
  visible, onClose, modalName,
  data, setData,
  customer
}) => {
  const headers = [
    'DO NUMBER', 
    'DO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'doNumber', 
    'doDate', 
    'amount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.doNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/DeliveryOrder/open/customer/${customer?.customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Delivery Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className={`px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition`}
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const GoodReceiptVendorMultipleSelect = ({
  visible, onClose, modalName,
  data, setData,
  vendor
}) => {
  const headers = [
    'GR NUMBER', 
    'GR DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'grNumber', 
    'grDate', 
    'amount', 
  ]

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.grNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GoodReceipt/open/vendor/${vendor?.vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRows)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRows([])
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableMultipleSelectMode
                  tableName={"Good Receipt"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const DeliveryOrderCustomerMultipleSelect = ({
  visible, onClose, modalName,
  data, setData,
  customer
}) => {
  const headers = [
    'DO NUMBER', 
    'DO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'doNumber', 
    'doDate', 
    'amount', 
  ]

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.doNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/DeliveryOrder/open/customer/${customer?.customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRows)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRows([])
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableMultipleSelectMode
                  tableName={"Delivery Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className={`px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition`}
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const SalesOrderCustomerSelect = ({
  visible, onClose, modalName,
  data, setData,
  customer
}) => {
  const headers = [
    'SO NUMBER', 
    'SO DATE', 
    'TOTAL AMOUNT', 
  ]
  const accessors = [
    'soNumber', 
    'soDate', 
    'totalAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.poNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/SalesOrder/open/customer/${customer?.customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Sales Order"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const ItemReturnSelect = ({
  visible, onClose, modalName,
  data, setData, referenceNumber, referenceType
}) => {

  const headers = [
    'Item', 
    'Quantity', 
    'Unit Price', 
    'Totals', 
  ]
  const accessors = [
    'item',  
    'quantity', 
    'unitPrice', 
    'totals', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.itemCode.toLowerCase().includes(lowerValue) ||
        item.itemDescription.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Item/item-return/reference-type/${referenceType}/reference/${referenceNumber}` , {
        headers: { 'Content-Type': 'application/json'}
      })

      let finalData = response.data
        
      setList(finalData)
      setFilteredData(finalData)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [referenceType, referenceNumber]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">

                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by description or code..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Item Return"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const BankChartOfAccountsSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Account Number', 
    'Account Name', 
    'Account Type', 
  ]
  const accessors = [
    'accountNumber', 
    'accountName', 
    'glAccountSubTypeDescription', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.accountNumber.toLowerCase().includes(lowerValue) ||
        item.accountName.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/GLAccount/bank` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"GL Account"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const VendorSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Vendor Id', 
    'Name 1', 
    'Name 2', 
  ]
  const accessors = [
    'vendorId', 
    'name1', 
    'name2', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.vendorId.toLowerCase().includes(lowerValue) ||
        item.name1.toLowerCase().includes(lowerValue) ||
        item.name2.toLowerCase().includes(lowerValue) 
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Vendor` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account name or code..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Vendor"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CustomerSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Customer Id', 
    'Name 1', 
    'Name 2', 
  ]
  const accessors = [
    'customerId', 
    'name1', 
    'name2', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.customerId.toLowerCase().includes(lowerValue) ||
        item.name1.toLowerCase().includes(lowerValue) ||
        item.name2.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Customer` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account name or code..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Customer"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const ItemSelect = ({
  visible, onClose, modalName,
  data, setData, isPurchase,
}) => {
  
// #region View Items
  const [selectedComponent, setSelectedComponent] = useState([]);
  const [showComponent, setShowComponent] = useState(false);
  
  const openViewComponentForm = () => {
    setShowComponent(true);
  }
  const closeViewComponentForm = () => {
    setShowComponent(false);
    setSelectedComponent([]);
  }

  const fetchDataById = useCallback(async (_data) => {
    try {
      const response = await axios.get(`${apiUrl}/api/Item/${_data?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  }, []);

  const viewComponentHandleClick = async (data) => {
    setLoading(true);

    const _data = await fetchDataById(data)

    setSelectedComponent(_data?.itemComponents || []);

    setLoading(false)
    openViewComponentForm();
  }
// #endregion

  const headers = [
    'Item Code', 
    'Description', 
    'UOM', 
    'Brand', 
    'Category', 
    'Components', 
  ]
  const accessors = [
    'itemCode', 
    'itemDescription', 
    'uomName', 
    'brandName', 
    'categoryName', 
    'components', 
  ]

  const actions = [
    { 
      name: "View Components", 
      tooltip: "view",
      icon: <ListAltIcon fontSize="small" />,
      className: "bg-sky-500 hover:bg-sky-600",
      function: viewComponentHandleClick,
      visible: false,
    },
  ];

  //#region Filters
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [productType, setProductType] = useState(null);
  const openFilterForm = () => {
    setShowFilterForm(true);
  }
  const closeFilterForm = () => {
    setShowFilterForm(false);
  }
  //#endregion

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.itemCode.toLowerCase().includes(lowerValue) ||
        item.itemDescription.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Item/product-type/${productType ? productType : "All"}` , {
        headers: { 'Content-Type': 'application/json'}
      })

      let finalData = response.data

      if(isPurchase){
        finalData = finalData.filter(i => !i.isBOM)
      }
        
      setList(finalData)
      setFilteredData(finalData)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [productType, isPurchase]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                <button className="bg-white border border-gray-300 text-theme-color hover:bg-gray-100 px-3 py-1 rounded-md flex items-center gap-2 hover:bg-opacity-90 transition"
                  onClick={openFilterForm}
                >
                  <FilterAltIcon/> Filter
                </button>

                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by description or code..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Items"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={actions}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ViewComponentModal 
        visible={showComponent} 
        onClose={closeViewComponentForm} 
        data={selectedComponent}
        modalName={"Components"}
      />

      <ItemFilterModal 
        visible={showFilterForm}
        onClose={closeFilterForm}
        modalName={"Filter"}

        productType={productType} setProductType={setProductType}
      />

      { loading && <Loading />}
    </>
  );
};
export const BOMItemSelect = ({
  visible, onClose, modalName,
  data, setData,
}) => {
  
// #region View Items
  const [selectedComponent, setSelectedComponent] = useState([]);
  const [showComponent, setShowComponent] = useState(false);
  
  const openViewComponentForm = () => {
    setShowComponent(true);
  }
  const closeViewComponentForm = () => {
    setShowComponent(false);
    setSelectedComponent([]);
  }

  const fetchDataById = useCallback(async (_data) => {
    try {
      const response = await axios.get(`${apiUrl}/api/Item/${_data?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  }, []);

  const viewComponentHandleClick = async (data) => {
    setLoading(true);

    const _data = await fetchDataById(data)

    setSelectedComponent(_data?.itemComponents || []);

    setLoading(false)
    openViewComponentForm();
  }
// #endregion

  const headers = [
    'Item Code', 
    'Description', 
    'UOM', 
    'Brand', 
    'Category', 
    'Components', 
  ]
  const accessors = [
    'itemCode', 
    'itemDescription', 
    'uomName', 
    'brandName', 
    'categoryName', 
    'components', 
  ]

  const actions = [
    { 
      name: "View Components", 
      tooltip: "view",
      icon: <ListAltIcon fontSize="small" />,
      className: "bg-sky-500 hover:bg-sky-600",
      function: viewComponentHandleClick,
      visible: false,
    },
  ];

  //#region Filters
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [productType, setProductType] = useState(null);
  const openFilterForm = () => {
    setShowFilterForm(true);
  }
  const closeFilterForm = () => {
    setShowFilterForm(false);
  }
  //#endregion

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.itemCode.toLowerCase().includes(lowerValue) ||
        item.itemDescription.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Item/product-type/${productType ? productType : "All"}` , {
        headers: { 'Content-Type': 'application/json'}
      })

      let finalData = response.data?.filter(i => {
        console.log({i})
        return i.isBOM
      })
        
      setList(finalData)
      setFilteredData(finalData)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [productType]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-7xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                <button className="bg-white border border-gray-300 text-theme-color hover:bg-gray-100 px-3 py-1 rounded-md flex items-center gap-2 hover:bg-opacity-90 transition"
                  onClick={openFilterForm}
                >
                  <FilterAltIcon/> Filter
                </button>

                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by description or code..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Items"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={actions}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ViewComponentModal 
        visible={showComponent} 
        onClose={closeViewComponentForm} 
        data={selectedComponent}
        modalName={"Components"}
      />

      <ItemFilterModal 
        visible={showFilterForm}
        onClose={closeFilterForm}
        modalName={"Filter"}

        productType={productType} setProductType={setProductType}
      />

      { loading && <Loading />}
    </>
  );
};
const ViewComponentModal = ({
  visible, onClose, modalName,
  data,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const headers = [
    'CODE', 
    'DESCRIPTION', 
    'BRAND', 
    'QTY', 
    'UNIT OF MEASUREMENT', 
    'CATEGORY', 
  ]
  const accessors = [
    'componentItemCode', 
    'componentItemDescription', 
    'componentBrandName', 
    'quantity', 
    'uomName', 
    'componentCategoryName', 
  ]

  if (!visible) return null;

  const resetLocalVariables = () => {
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-2xl shadow-lg w-full max-w-4xl max-h-[90vh]  p-8 flex flex-col overflow-hidden"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>
              <div className="max-w-4xl mx-auto p-4 md:p-8 bg-white rounded-xl shadow-sm overflow-y-auto custom-scrollbar flex flex-col gap-5">
                <CreateTable
                  tableName={"Item Components"}
                  headers={headers}
                  accessors={accessors}
                  data={data || []}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={data?.length || 0}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
const ItemFilterModal = ({
  visible, onClose, modalName,
  productType, setProductType,
}) => {
  const [failMessage, setFailMessage] = useState("");
  const [_productType, _setProductType] = useState(productType);

  useEffect(() => {
    if(visible){
      _setProductType(productType)
    }
  }, [visible, productType ])

  const handleSubmit = async () => {
    setProductType(_productType);
    closeModal();
  };

  const resetLocalVariables = () => {
    setFailMessage("");
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-2xl shadow-lg w-full max-w-lg p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    Product Type
                  </label>
                  <ProductTypeSelect data={_productType} setData={_setProductType} isEditable={true}/>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alert */}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col space-y-2">
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>
    </>
  );
};
export const SalesmanSelect = ({data, setData, customer, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      if(!customer) return;

      try {
        const response = await axios.get(`${apiUrl}/api/Customer/${customer?.id}/salesman` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, [customer]);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    useEffect(() => {
      if(!customer){
        setData(null)
      }
    }, [customer, setData])

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose salesman </option>
                    {_data.map((item) => (
                    <option key={item.id} value={item.id} className='text-gray-700'>
                        {item.name}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const AccessTemplateSelect = ({data, setData, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/AccessTemplate/active` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, []);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose template </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {description}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const ReportsJournalSelect = ({
  visible, onClose, modalName,
  documentTypeId,
  data, setData
}) => {
  const headers = [
    'Document', 
    'Date', 
    'Debit', 
    'Credit', 
  ]
  const accessors = [
    'documentHeaderText', 
    'postingDate', 
    'totalDebitAmount', 
    'totalCreditAmount', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.documentHeaderText.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Journal/reports/documentTypeId/${documentTypeId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [documentTypeId]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account name or code..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Journals"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const BankTypeSelect = ({ data, setData, isEditable }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleSelectChange = (event) => {
    setIsSelected(true);
    setData(event.target.value);
  };

  useEffect(() => {
    setIsSelected(!!data && data !== 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-1">
        <div>
            <select
                value={data || ""}
                onChange={handleSelectChange}
                disabled={!isEditable}
                className={`w-full border rounded-md p-2 text-sm border-gray-300 outline-none focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                    ${isSelected ? "text-gray-700" : "text-gray-400"}
                `}
            >
                <option value="">
                    Choose type
                </option>
                <option value="Savings" className="text-gray-700">
                    Savings
                </option>
                <option value="Checking" className="text-gray-700">
                    Checking
                </option>
                <option value="eWallet" className="text-gray-700">
                    eWallet
                </option>
            </select>
        </div>
    </div>
  );
};
export const BankSelect = ({
  visible, onClose, modalName,
  data, setData
}) => {
  const headers = [
    'Bank Name', 
    'Bank Number', 
    'Bank Type', 
  ]
  const accessors = [
    'bankName', 
    'accountNumber', 
    'accountType', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.bankName.toLowerCase().includes(lowerValue) ||
        item.accountNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Bank` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Bank"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const BankByGLAccountSelect = ({
  visible, onClose, modalName,
  glAccount,
  data, setData
}) => {
  const headers = [
    'Bank Name', 
    'Bank Number', 
    'Bank Type', 
  ]
  const accessors = [
    'bankName', 
    'accountNumber', 
    'accountType', 
  ]

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.bankName.toLowerCase().includes(lowerValue) ||
        item.accountNumber.toLowerCase().includes(lowerValue)
    );

    setList(filtered);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Bank/account/${glAccount?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setList(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      setList([])
    } finally {
      setLoading(false);
    }
  }, [glAccount]);

  useEffect(() => {
    if(visible){
      fetchData();
    }
  }, [visible, fetchData])

  const handleSubmit = async () => {
    setData(selectedRow)
    closeModal();
  };

  const resetLocalVariables = () => {
    setCurrentPage(1)
    setSelectedRow(null)
    setLoading(false)
    setFilteredData([])
    setList([])
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  if (!visible) return null;

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-xl shadow-lg w-full max-w-3xl p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                {/* Search and Filters */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by account number or name..."
                    className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {/* Add filter dropdowns here if needed */}
                </div>

                <CreateTableSelectMode
                  tableName={"Bank"}
                  headers={headers}
                  accessors={accessors}
                  data={list}
                  actions={[]}

                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={10}
                  totalItems={list?.length}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Select
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      { loading && <Loading />}
    </>
  );
};
export const CheckbookSelect = ({data, setData, bank, referenceNumber, setReferenceNumber, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
          setReferenceNumber(selectedData?.currentNumberFormat ?? "")
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      if(!bank){
        _setData([])
        setData(null)
        setReferenceNumber("")
        return
      }

      try {
        const response = await axios.get(`${apiUrl}/api/Checkbook/bank/${bank?.id}` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
        _setData([])
        setData(null)
        setReferenceNumber("")
      }
    }, [bank, setData, setReferenceNumber]);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose checkbook </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const CheckbookFromUpdateSelect = ({oldData, data, setData, bank, referenceNumber, setReferenceNumber, isEditable}) => {
    const [isSelected, setIsSelected] = useState(false);
    const [_data, _setData] = useState([]);

    const handleSelectChange = (event) => {
        setIsSelected(true);

        const id_value = event.target.value;
        const selectedData = _data.find((temp) => temp.id.toString() === id_value.toString());

        if (selectedData) {
          setData(selectedData);
          if(selectedData?.id?.toString() === oldData?.checkbookTransaction?.checkbookId?.toString() && oldData?.checkbookTransaction?.status === "Issued"){
            setReferenceNumber(oldData?.checkbookTransaction?.checkbookNumber ?? "")
          }
          else {
            setReferenceNumber(selectedData?.currentNumberFormat ?? "")
          }
        } else {
          setData(null);
        }
    };

    const fetchData = useCallback(async () => {
      if(!bank){
        _setData([])
        setData(null)
        setReferenceNumber("")
        return
      }

      try {
        const response = await axios.get(`${apiUrl}/api/Checkbook/bank/${bank?.id}` , {
          headers: { 'Content-Type': 'application/json'}
        })
          
        _setData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error);
        _setData([])
        setData(null)
        setReferenceNumber("")
      }
    }, [bank, setData, setReferenceNumber]);

    useEffect(() => {
        fetchData();
        setIsSelected(!!data && data !== 0);
    }, [data, fetchData]);

    return (
        <div className="flex flex-col gap-1">
            <div>
                <select
                    value={data?.id || ""}
                    onChange={handleSelectChange}
                    disabled={!isEditable}
                    className={`w-full border rounded-md p-2 border-gray-300 outline-none text-sm focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color
                        ${isSelected ? "text-gray-700" : "text-gray-400"}
                    `}
                >
                    <option value=""> Choose checkbook </option>
                    {_data.map(({ id, description }) => (
                    <option key={id} value={id} className='text-gray-700'>
                        {`${description}`}
                    </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
export const PaymentReferenceTypeModal = ({
  visible,
  onClose,
  handleOrderSubmit,
  handleInvoiceSubmit,
  modalName = "Select Payment Reference",
}) => {
  const [selectedType, setSelectedType] = useState("")
  if (!visible) return null;

  const handleSubmit = async () => {
    if(!selectedType){
      return;
    }
    if(selectedType === "Order") {
      await handleOrderSubmit();
    }
    if(selectedType === "Invoice") {
      await handleInvoiceSubmit();
    }
    onClose();
  };

  const disableOrder = true;

  return (
    <div className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">

        {/* Header */}
        <h2 className="text-2xl font-semibold text-gray-800">
          {modalName}
        </h2>

        <p className="text-sm text-gray-500 mt-2">
          Select the document that this payment will be applied to.
        </p>

        {/* Selection */}
        <div className="space-y-3 mt-8">

          {/* Delivery Order */}
          <button
              type="button"
              disabled={disableOrder}
              onClick={() => setSelectedType("Order")}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
                ${
                  selectedType === "Order"
                    ? "border-theme-color bg-theme-color/5"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent
              `}
            >
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center
                ${
                  selectedType === "Order"
                    ? "bg-theme-color text-white"
                    : "bg-gray-100 text-gray-500"
                }
              `}
            >
              <LocalShippingIcon />
            </div>

            <div className="flex-1">
              <div className="font-bold text-gray-800">
                Delivery Order
              </div>

              <div className="text-xs text-gray-500 mt-1">
                Tag this payment to a Delivery Order
              </div>
            </div>

            {selectedType === "Order" && (
              <CheckCircleIcon className="text-theme-color" />
            )}
          </button>

          {/* Sales Invoice */}
          <button
            type="button"
            onClick={() => setSelectedType("Invoice")}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
              ${
                selectedType === "Invoice"
                  ? "border-theme-color bg-theme-color/5"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }
            `}
          >
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center
                ${
                  selectedType === "Invoice"
                    ? "bg-theme-color text-white"
                    : "bg-gray-100 text-gray-500"
                }
              `}
            >
              <ReceiptLongIcon />
            </div>

            <div className="flex-1">
              <div className="font-bold text-gray-800">
                Sales Invoice
              </div>

              <div className="text-xs text-gray-500 mt-1">
                Tag this payment to a Sales Invoice
              </div>
            </div>

            {selectedType === "Invoice" && (
              <CheckCircleIcon className="text-theme-color" />
            )}
          </button>

        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-8">

          <button
            type="button"
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={!selectedType}
            className={`px-5 py-2 rounded-md text-white transition
              ${
                selectedType
                  ? "bg-theme-color hover:bg-theme-color-h"
                  : "bg-gray-300 cursor-not-allowed"
              }
            `}
            onClick={handleSubmit}
          >
            Continue
          </button>

        </div>
      </div>
    </div>
  );
};
//#endregion













export const GetAccountConfiguration = async (key, type) => {
  try {
    const response = await axios.get(`${apiUrl}/api/GLAccountConfiguration/key/${key}/type/${type}` , {
      headers: { 'Content-Type': 'application/json'}
    })
      
    return response.data
  } catch (error) {
    console.error('Error fetching data:', error);
    return null
  }
};